import React, { useContext, useState, useEffect, useMemo } from 'react';
import { BoxContext } from './BoxContext';
import { COORDINATOR_ROLES } from './contexts/RoleContext';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { useToast } from './hooks/use-toast';
import toast from 'react-hot-toast';
import useBarcodeScanner from './hooks/useBarcodeScanner';
import BarcodeScannerOverlay from './components/BarcodeScannerOverlay';
import WeightInputModal from './components/WeightInputModal';
import AddItem from './AddItem';
import ExpirationDatePicker from './components/ExpirationDatePicker';
import {
  ChevronDown,
  Calculator,
  Copy,
  Trash2,
  X,
  Package,
  Edit2,
  Edit3,
  Check,
  Settings,
  Plus,
  Minus,
  AlertTriangle,
  Sparkles,
  ClipboardCopy,
  Scan
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './components/ui/dialog';

const API_URL = import.meta.env.VITE_API_URL;

// Helper to calculate stats per box
const getBoxStats = (box) => {
  const totalUnits = box.items.reduce((acc, item) => acc + Number(item.QTY), 0);
  const totalSkus = box.items.length;
  return { totalUnits, totalSkus };
};

// Helper to check if box has validation errors
const getBoxValidationErrors = (box, clientCountry, shipmentItems, effectiveAsinData) => {
  const errors = [];
  
  // Check missing dimensions
  if (!box.length || box.length <= 0) errors.push('Missing length');
  if (!box.width || box.width <= 0) errors.push('Missing width');
  if (!box.height || box.height <= 0) errors.push('Missing height');
  
  // Check missing or invalid weight
  if (!box.weight || box.weight <= 0) {
    errors.push('Missing weight');
  } else if (box.weight < 0.3) {
    errors.push('Weight too low (<0.3kg)');
  } else if (box.weight > 21) {
    errors.push('CRITICAL: Weight exceeds 21kg limit!');
  }
  
  // Helper function to check for valid weight
  const isValidWeight = (weight) => {
    const num = parseFloat(weight);
    return !isNaN(num) && num > 0;
  };

  // Calculate expected weight from items (similar to calcAutoWeight in BoxSidebar)
  const calculateExpectedWeight = () => {
    if (!box.items || !effectiveAsinData) return null;
    
    let hasAllWeights = true;
    const itemsTotalWeight = box.items.reduce((acc, item) => {
      const asinRow = effectiveAsinData.find(a => a.ASIN === item.ASIN);
      
      let unitWeight = null;
      
      // Prioritize weight from asinData if it's valid
      if (asinRow && isValidWeight(asinRow.WEIGHT)) {
        unitWeight = parseFloat(asinRow.WEIGHT);
      } 
      // Fallback to the item's own WEIGHT property if it's valid
      else if (isValidWeight(item.WEIGHT)) {
        unitWeight = parseFloat(item.WEIGHT);
      }

      if (unitWeight !== null) {
        const quantity = parseInt(item.QTY, 10) || 0;
        const itemTotal = unitWeight * quantity;
        return acc + itemTotal;
      } else {
        hasAllWeights = false;
        return acc;
      }
    }, 0);

    if (!hasAllWeights) return null;
    
    const boxWeight = 1; // 1kg for the box itself
    return itemsTotalWeight + boxWeight;
  };

  // Check for weight mismatch
  const expectedWeight = calculateExpectedWeight();
  const currentWeight = parseFloat(box.weight) || 0;
  
  if (expectedWeight && currentWeight > 0) {
    const weightDifference = Math.abs(currentWeight - expectedWeight);
    if (weightDifference > 0.1) { // 0.1kg tolerance
      errors.push(`Weight mismatch: Manual ${currentWeight}kg vs Calculated ${expectedWeight.toFixed(2)}kg`);
    }
  }
  
  // Calculate total box value from items
  const calculateBoxValue = () => {
    if (!box.items) return 0;
    
    let total = 0;
    
    box.items.forEach(item => {
      const asinDataItem = effectiveAsinData?.find(data => data.ASIN === item.ASIN);
      const value = asinDataItem?.VALUE;
      const qty = parseInt(item.QTY || 0);
      
      if (value && !isNaN(parseFloat(value)) && qty > 0) {
        const itemTotal = parseFloat(value) * qty;
        total += itemTotal;
      }
    });
    
    return total;
  };

  const totalValue = calculateBoxValue();
  
  // Check if box value exceeds $800 (only for US shipments)
  const isUSShipment = clientCountry && (
    clientCountry.toUpperCase() === 'US' || 
    clientCountry.toUpperCase() === 'USA' || 
    clientCountry.toUpperCase() === 'UNITED STATES'
  );
  
  if (isUSShipment && totalValue > 800) {
    errors.push(`Value exceeds $800 (${totalValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })})`);
  }
  
  return errors;
};

// Helper to generate a unique signature for a box based on dimensions and items
const getBoxSignature = (box) => {
  const dims = `${box.length || 0}x${box.width || 0}x${box.height || 0}x${box.weight || 0}`;
  const items = [...box.items]
    .sort((a, b) => (a.ASIN || '').localeCompare(b.ASIN || ''))
    .map(item => `${item.ASIN || ''}:${item.QTY}`)
    .join('|');
  return `${dims}:${items}`;
};

// Helper to generate consistent colors for box signatures with contrasting sequences
const getBoxColor = (signature, allBoxes, currentIndex) => {
  const colors = [
    { bg: 'bg-blue-500', text: 'text-white', lightBg: 'bg-blue-200', border: 'border-blue-200' },
    { bg: 'bg-green-500', text: 'text-white', lightBg: 'bg-green-200', border: 'border-green-200' },
    { bg: 'bg-purple-500', text: 'text-white', lightBg: 'bg-purple-200', border: 'border-purple-200' },
    { bg: 'bg-orange-500', text: 'text-white', lightBg: 'bg-orange-200', border: 'border-orange-200' },
    { bg: 'bg-pink-500', text: 'text-white', lightBg: 'bg-pink-200', border: 'border-pink-200' },
    { bg: 'bg-indigo-500', text: 'text-white', lightBg: 'bg-indigo-200', border: 'border-indigo-200' },
    { bg: 'bg-red-500', text: 'text-white', lightBg: 'bg-red-200', border: 'border-red-200' },
    { bg: 'bg-teal-500', text: 'text-white', lightBg: 'bg-teal-200', border: 'border-teal-200' },
    { bg: 'bg-yellow-500', text: 'text-black', lightBg: 'bg-yellow-200', border: 'border-yellow-200' },
    { bg: 'bg-cyan-500', text: 'text-white', lightBg: 'bg-cyan-200', border: 'border-cyan-200' },
  ];
  
  // Get unique signatures in order they appear
  const uniqueSignatures = [];
  const signatureMap = new Map();
  
  allBoxes?.forEach((box, index) => {
    const boxSig = getBoxSignature(box);
    if (!signatureMap.has(boxSig)) {
      signatureMap.set(boxSig, uniqueSignatures.length);
      uniqueSignatures.push(boxSig);
    }
  });
  
  // Get the index of this signature in the sequence
  const signatureIndex = signatureMap.get(signature) || 0;
  
  // Use contrasting colors by spacing them out
  const colorIndex = signatureIndex % colors.length;
  return colors[colorIndex];
};

// Helper to group consecutive identical boxes
const groupConsecutiveIdenticalBoxes = (boxes) => {
  if (!boxes || boxes.length === 0) return [];
  
  const groups = [];
  let currentGroup = {
    signature: getBoxSignature(boxes[0]),
    boxes: [boxes[0]]
  };
  
  for (let i = 1; i < boxes.length; i++) {
    const boxSignature = getBoxSignature(boxes[i]);
    
    if (boxSignature === currentGroup.signature) {
      // Same signature, add to current group
      currentGroup.boxes.push(boxes[i]);
    } else {
      // Different signature, start new group
      groups.push(currentGroup);
      currentGroup = {
        signature: boxSignature,
        boxes: [boxes[i]]
      };
    }
  }
  
  // Don't forget the last group
  groups.push(currentGroup);
  
  return groups;
};

const BoxListItem = ({ box, selected, onClick, shipmentItems, asinData, allBoxes, onHoverChange, clientCountry, standardBoxes, isLTL, onPalletChange }) => {
  const { totalUnits, totalSkus } = getBoxStats(box);
  const visualId = box.visualId || `#${box.boxNumber}`;
  
  // Get validation errors for this box
  const validationErrors = getBoxValidationErrors(box, clientCountry, shipmentItems, asinData);
  const hasErrors = validationErrors.length > 0;
  
  // Check if box matches a preset by comparing dimensions (same logic as BoxSidebar)
  const getMatchingPreset = () => {
    if (!standardBoxes || !standardBoxes.length || !box.length || !box.width || !box.height) {
      return null;
    }
    
    const currentLength = parseFloat(box.length);
    const currentWidth = parseFloat(box.width);
    const currentHeight = parseFloat(box.height);
    
    const matchingPreset = standardBoxes.find(preset => {
      const presetLength = parseFloat(preset.LENGTH);
      const presetWidth = parseFloat(preset.WIDTH);
      const presetHeight = parseFloat(preset.HEIGHT);
      
      return presetLength === currentLength && 
             presetWidth === currentWidth && 
             presetHeight === currentHeight;
    });
    
    return matchingPreset;
  };
  
  const matchingPreset = getMatchingPreset();
  
  // Get color coding for this box based on its signature
  const boxSignature = getBoxSignature(box);
  const boxColor = getBoxColor(boxSignature, allBoxes, 0);

  // Get image URL for ASIN
  const getImageUrl = (asin) => {
    const matchedItem = shipmentItems?.find(item => item.item.asin === asin);
    return (matchedItem && matchedItem.item.images && matchedItem.item.images.length > 0 && matchedItem.item.images[0].large_url) 
      || 'https://qph.cf2.quoracdn.net/main-qimg-654617264f9192ec976abe6e53356240-lq';
  };

  // Get quantity for ASIN
  const getQuantity = (asin) => {
    return box.items.reduce((acc, item) => item.ASIN === asin ? acc + Number(item.QTY) : acc, 0);
  };

  // Get item details for hover preview
  const getItemDetails = (asin) => {
    const matchedItem = shipmentItems?.find(item => item.item.asin === asin);
    const boxItem = box.items.find(item => item.ASIN === asin);
    return {
      title: matchedItem?.item?.title || boxItem?.TITLE || 'Unknown Item',
      sku: matchedItem?.item?.merchant_sku || boxItem?.SKU || 'Unknown SKU',
      imageUrl: getImageUrl(asin),
      quantity: getQuantity(asin)
    };
  };

  // Handle mouse enter for hover preview
  const handleMouseEnter = (e) => {
    if (box.items.length > 0 && onHoverChange) {
      const rect = e.currentTarget.getBoundingClientRect();
      const tooltipWidth = 320;
      const tooltipHeight = 250; // Estimated height for decision making
      const padding = 10;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let x, y, bottom, placement;
      let arrowOffset, arrowVerticalOffset;

      // --- Horizontal Positioning ---
      const fitsRight = rect.right + padding + tooltipWidth <= viewportWidth;
      const fitsLeft = rect.left - padding - tooltipWidth >= 0;

      if (fitsRight) {
        placement = 'right';
        x = rect.right + padding;
      } else if (fitsLeft) {
        placement = 'left';
        x = rect.left - padding - tooltipWidth;
      } else {
        placement = 'center'; // Fallback
        x = rect.left + (rect.width - tooltipWidth) / 2;
        if (x < padding) x = padding;
        if (x + tooltipWidth > viewportWidth - padding) {
          x = viewportWidth - tooltipWidth - padding;
        }
      }

      // --- Vertical Positioning ---
      if (placement === 'right' || placement === 'left') {
        // For side placements, try to center vertically, then adjust to fit viewport
        y = (rect.top + rect.height / 2) - (tooltipHeight / 2);

        if (y < padding) {
          y = padding;
        } else if (y + tooltipHeight > viewportHeight - padding) {
          y = viewportHeight - tooltipHeight - padding;
        }

        // Calculate where the arrow should be vertically relative to the tooltip
        arrowVerticalOffset = (rect.top + rect.height / 2) - y;
      } else { // For 'center' placement, decide above or below
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        // Prefer to open below unless there is not enough space
        if (spaceBelow >= tooltipHeight + padding || spaceBelow >= spaceAbove) {
          placement = 'below';
          y = rect.bottom + padding;
        } else {
          placement = 'above';
          // Anchor the bottom of the tooltip instead of the top
          bottom = viewportHeight - rect.top + padding;
        }
        // Calculate where the arrow should be horizontally relative to the tooltip
        arrowOffset = (rect.left + rect.width / 2) - x;
      }

      onHoverChange({
        box,
        position: { x, y, bottom, placement, arrowOffset, arrowVerticalOffset },
        getItemDetails,
        boxSignature,
        allBoxes,
        validationErrors,
      });
    }
  };

  const handleMouseLeave = () => {
    if (onHoverChange) {
      onHoverChange(null);
    }
  };

  // Get unique ASINs and their details for product thumbnails
  const uniqueAsins = [...new Set(box.items.map(item => item.ASIN))];
  const maxVisibleThumbnails = 4;
  const visibleAsins = uniqueAsins.slice(0, maxVisibleThumbnails);
  const remainingCount = uniqueAsins.length - maxVisibleThumbnails;

  return (
    <div
      className={`w-full h-12 flex items-center cursor-pointer transition-colors duration-150 relative px-3 border-l-4 ${
        hasErrors && !selected
          ? 'border-l-red-500 bg-red-50 hover:bg-red-100'
          : selected 
            ? 'border-l-blue-500 bg-blue-50' 
            : `${boxColor.border.replace('border-', 'border-l-')} ${boxColor.lightBg} hover:bg-slate-100`
      }`}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Left: Box Info */}
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="text-lg">📦</div>
                      <div className="flex items-center gap-1">
            <div className="text-sm font-bold text-slate-900">
              BOX {box.boxNumber}
              {isLTL && box.PALLET && (
                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                  Pallet {box.PALLET}
                </span>
              )}
            </div>
            {hasErrors && (
              <span className="text-red-500 text-sm">⚠</span>
            )}
          </div>
        </div>
        
        {/* Box dimensions/preset info */}
        <div className="min-w-0 flex-1">
          {matchingPreset ? (
            <div className="text-sm text-blue-600 font-medium truncate">
              {matchingPreset.NAME}
              {box.weight && ` • ${Math.round(box.weight)} kg`}
            </div>
          ) : (box.length && box.width && box.height) ? (
            <div className="text-sm text-slate-600 font-medium truncate">
              {Math.round(box.length)}×{Math.round(box.width)}×{Math.round(box.height)}
              {box.weight && ` • ${Math.round(box.weight)} kg`}
            </div>
          ) : (
            <div className="text-sm text-slate-400">No dimensions set</div>
          )}
        </div>
      </div>

      {/* Center: Product thumbnails */}
      <div className="flex items-center gap-1 mx-4">
        {totalUnits > 0 && uniqueAsins.length > 0 ? (
          <>
            {visibleAsins.map((asin, index) => (
              <div
                key={asin}
                className="w-8 h-8 border border-slate-300 bg-white overflow-hidden flex-shrink-0"
                style={{ zIndex: visibleAsins.length - index }}
              >
                <img 
                  src={getImageUrl(asin)} 
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            {remainingCount > 0 && (
              <div className="w-8 h-8 bg-slate-400 text-white text-xs flex items-center justify-center font-bold">
                +{remainingCount}
              </div>
            )}
          </>
        ) : (
          <div className="w-8 h-8 bg-slate-100 border border-slate-300 flex items-center justify-center">
            <span className="text-slate-400 text-xs">📦</span>
          </div>
        )}
      </div>

            {/* Right: Units/SKUs, Value, and Error */}
      <div className="flex items-center gap-3">
        <div className="text-sm text-slate-700 text-right min-w-20">
          {totalUnits === 0 ? (
            <span className="text-orange-600 font-normal">Empty</span>
          ) : (
            <div className="text-green-700 font-normal">
              <div>{totalUnits} unit{totalUnits !== 1 ? 's' : ''}</div>
              <div className="text-xs text-slate-500">{totalSkus} SKU{totalSkus !== 1 ? 's' : ''}</div>
            </div>
          )}
        </div>

        {/* Box Value (US only) */}
        {(() => {
          const isUSShipment = clientCountry && (
            clientCountry.toUpperCase() === 'US' || 
            clientCountry.toUpperCase() === 'USA' || 
            clientCountry.toUpperCase() === 'UNITED STATES'
          );
          
          if (isUSShipment && box.items && box.items.length > 0) {
            let totalValue = 0;
            let hasAnyValue = false;
            
            box.items.forEach(item => {
              const asinDataItem = asinData?.find(data => data.ASIN === item.ASIN);
              const value = asinDataItem?.VALUE;
              
              if (value && !isNaN(parseFloat(value))) {
                totalValue += parseFloat(value) * parseInt(item.QTY || 0);
                hasAnyValue = true;
              }
            });
            
            const isOverLimit = totalValue > 800;
            
            return (
              <div className={`text-xs px-2 py-1 text-center min-w-16 ${
                isOverLimit 
                  ? 'bg-red-600 text-white' 
                  : hasAnyValue
                    ? 'bg-green-600 text-white'
                    : 'bg-yellow-600 text-white'
              }`}>
                {isOverLimit && '⚠ '}
                {!hasAnyValue && '? '}
                {hasAnyValue ? `$${Math.round(totalValue)}` : 'Unknown'}
                {isOverLimit && ' OVER'}
              </div>
            );
          }
          
          return null;
        })()}


      </div>
    </div>
  );
};

// Box Accordion Component
const BoxAccordion = ({
  box,
  onDuplicateBox,
  onDeleteBox,
  onUpdateBoxDetails,
  shipmentItems,
  asinData,
  selectedNickname,
  clientCountry,
  standardBoxes,
  fetchStandardBoxes,
  shipmentId,
  onClose,
  setShipmentItems,
  canEditDetails,
  isLTL,
  onPalletChange,
  lastScannedItemSku
}) => {
  const { 
    boxes, 
    refreshBoxes, 
    standardBoxes: contextStandardBoxes, 
    refreshStandardBoxes,
    clientInfo,
    reloadShipmentData,
    reloadAsinData,
    selectedPlan,
    asinData: contextAsinData,
  } = useContext(BoxContext);

  const availableStandardBoxes = (standardBoxes && standardBoxes.length > 0) ? standardBoxes : (contextStandardBoxes || []);
  const effectiveAsinData = (asinData && asinData.length > 0) ? asinData : (contextAsinData || []);
  const currentBox = boxes.find(b => b.boxNumber === box.boxNumber) || box;
  

  const [dims, setDims] = useState({
    length: currentBox.length || '',
    width: currentBox.width || '',
    height: currentBox.height || '',
    weight: currentBox.weight || ''
  });
  const [palletValue, setPalletValue] = useState(currentBox.PALLET || '1');

  // Update local state when currentBox.PALLET changes
  useEffect(() => {
    setPalletValue(currentBox.PALLET || '1');
  }, [currentBox.PALLET]);
  const [showPresetSelect, setShowPresetSelect] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(1);
  const [dimsSaving, setDimsSaving] = useState({});
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [focusedFields, setFocusedFields] = useState({});

  const [editingItemField, setEditingItemField] = useState({});
  const [editingValues, setEditingValues] = useState({});
  const [showAddPreset, setShowAddPreset] = useState(false);
  const [newPreset, setNewPreset] = useState({ name: '', length: '', width: '', height: '', chargeable: false });

  const { toast } = useToast();

  const showToast = (message, variant = 'default') => {
    const toastVariant = variant === 'warning' ? 'warning' : 
                        variant === 'error' ? 'error' : 
                        variant === 'success' ? 'success' : 
                        'default';
    
    toast({
      title: message,
      variant: toastVariant
    });
  };

  useEffect(() => {
    setDims(prevDims => ({
      // Only update dimensions if they're not currently focused AND not currently saving
      length: (focusedFields.length || dimsSaving.length) ? prevDims.length : (currentBox.length || ''),
      width: (focusedFields.width || dimsSaving.width) ? prevDims.width : (currentBox.width || ''),
      height: (focusedFields.height || dimsSaving.height) ? prevDims.height : (currentBox.height || ''),
      // Only update weight if not focused/saving and (no local value or initial load)
      weight: (focusedFields.weight || dimsSaving.weight) ? prevDims.weight : 
        (prevDims.weight === undefined || prevDims.weight === '' 
          ? (currentBox.weight || '') 
          : prevDims.weight)
    }));
  }, [currentBox, focusedFields, dimsSaving]);

  // Reset focus states when switching boxes
  useEffect(() => {
    setFocusedFields({});
  }, [box.boxNumber]);

  // Auto-select matching preset box when dimensions change
  useEffect(() => {
    if (currentBox && availableStandardBoxes && availableStandardBoxes.length > 0) {
      const currentLength = parseInt(currentBox.length) || 0;
      const currentWidth = parseInt(currentBox.width) || 0;
      const currentHeight = parseInt(currentBox.height) || 0;
      
      const matchingPreset = availableStandardBoxes.find(preset => {
        const presetLength = parseInt(preset.LENGTH) || 0;
        const presetWidth = parseInt(preset.WIDTH) || 0;
        const presetHeight = parseInt(preset.HEIGHT) || 0;
        
        return presetLength === currentLength && 
               presetWidth === currentWidth && 
               presetHeight === currentHeight;
      });
      
      if (matchingPreset) {
        setSelectedPreset(matchingPreset.NAME);
      } else {
        setSelectedPreset(null);
      }
    }
  }, [currentBox?.length, currentBox?.width, currentBox?.height, availableStandardBoxes]);

  const totalUnits = currentBox.items.reduce((a,i)=>a+Number(i.QTY),0);
  const totalSkus = currentBox.items.length;

  const calcAutoWeight = () => {
    let hasAllWeights = true;
    let missingWeightItems = [];
    
    const isValidWeight = (weight) => {
      const num = parseFloat(weight);
      return !isNaN(num) && num > 0;
    };

    const itemsTotalWeight = currentBox.items.reduce((acc, item) => {
      const asinRow = effectiveAsinData.find(a => a.ASIN === item.ASIN);
      
      let unitWeight = null;
      
      if (asinRow && isValidWeight(asinRow.WEIGHT)) {
        unitWeight = parseFloat(asinRow.WEIGHT);
      } else if (isValidWeight(item.WEIGHT)) {
        unitWeight = parseFloat(item.WEIGHT);
      }

      if (unitWeight !== null) {
        const quantity = parseInt(item.QTY, 10) || 0;
        const itemTotal = unitWeight * quantity;
        return acc + itemTotal;
      } else {
        hasAllWeights = false;
        missingWeightItems.push(item.ASIN || item.SKU || 'Unknown Item');
        return acc;
      }
    }, 0);

    const boxWeight = 1;
    const totalWeight = hasAllWeights ? (itemsTotalWeight + boxWeight).toFixed(2) : null;

    return {
      canCalculate: hasAllWeights,
      weight: totalWeight,
      missingItems: missingWeightItems
    };
  };

  const getAsinData = (asin) => {
    const data = effectiveAsinData.find(a => a.ASIN === asin) || {};
    return data;
  };

  const getProductInfo = (item, asinInfo) => {
    const shipmentItem = shipmentItems?.find(si => 
      si.item?.asin === item.ASIN || 
      si.item?.merchant_sku === item.SKU ||
      si.item?.fnsku === item.FNSKU
    );
    
    const title = asinInfo.TITLE || asinInfo.title || asinInfo.PRODUCT_NAME || asinInfo.productName || 
                  shipmentItem?.item?.title || item.TITLE || item.title || 'Product title not available';
    
    const image = asinInfo.IMAGE_URL || asinInfo.imageUrl || asinInfo.IMAGE || asinInfo.image || asinInfo.MAIN_IMAGE || 
                  (shipmentItem?.item?.images?.[0]?.large_url) || item.IMAGE_URL;
    
    const unitWeight = asinInfo.WEIGHT || asinInfo.weight || item.WEIGHT || 
                       (shipmentItem && getAsinData(shipmentItem.item.asin)?.WEIGHT) || '';
    
    const fnsku = asinInfo.FNSKU || asinInfo.fnsku || shipmentItem?.item?.fnsku || item.FNSKU || '';
    const sku = asinInfo.SKU || asinInfo.sku || shipmentItem?.item?.merchant_sku || item.SKU || '';
    const value = asinInfo.VALUE || asinInfo.value || '';
    
    return { title, image, weight: unitWeight, fnsku, sku, value };
  };

  const handleDimBlur = async (field, value) => {
    // For dimensions (length, width, height), use integers. For weight, keep as-is.
    const processedValue = value === '' ? null : 
      (field === 'length' || field === 'width' || field === 'height') ? parseInt(value) || null : value;
    
    if (processedValue !== currentBox[field]) {
      try {
        setDimsSaving(prev => ({ ...prev, [field]: true }));
        const updated = { ...currentBox, [field]: processedValue };
        await onUpdateBoxDetails(updated);
        
        if (selectedPreset && availableStandardBoxes && (field === 'length' || field === 'width' || field === 'height')) {
          const newDims = { ...currentBox, [field]: processedValue };
          const matchingPreset = availableStandardBoxes.find(preset => {
            const presetLength = parseInt(preset.LENGTH) || 0;
            const presetWidth = parseInt(preset.WIDTH) || 0;
            const presetHeight = parseInt(preset.HEIGHT) || 0;
            
            return presetLength === (parseInt(newDims.length) || 0) && 
                   presetWidth === (parseInt(newDims.width) || 0) && 
                   presetHeight === (parseInt(newDims.height) || 0);
          });
          
          if (!matchingPreset) {
            setSelectedPreset(null);
          }
        }
        
        // Don't immediately refresh boxes to avoid flickering during quick edits
        // The useEffect will handle updating from currentBox when not focused
        
        // Refresh boxes after a short delay
        setTimeout(() => {
          refreshBoxes();
        }, 100);
        
        // Clear saving state after a longer delay for visual feedback
        setTimeout(() => {
          setDimsSaving(prev => ({ ...prev, [field]: false }));
        }, 1500);
      } catch (error) {
        console.error(`Error updating ${field}:`, error);
        showToast(`Failed to update ${field}`, 'error');
        setDimsSaving(prev => ({ ...prev, [field]: false }));
      }
    }
  };

  const handleDuplicate = async () => {
    try {
      const cleanedBox = {
        ...currentBox,
        length: currentBox.length || null,
        width: currentBox.width || null,
        height: currentBox.height || null,
        weight: currentBox.weight || null,
        nickname: currentBox.nickname || null,
        items: currentBox.items || []
      };
      
      Object.keys(cleanedBox).forEach(key => {
        if (cleanedBox[key] === undefined) {
          cleanedBox[key] = null;
        }
      });
      
      await onDuplicateBox(cleanedBox, duplicateCount);
      refreshBoxes();
      
      showToast(`Successfully duplicated box ${duplicateCount} time${duplicateCount !== 1 ? 's' : ''}`, 'success');
      
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Error duplicating box:', error);
      showToast('Failed to duplicate box', 'error');
    }
  };



  const handleQuantityChange = async (index, newQty) => {
    try {
      const item = currentBox.items[index];
      const quantity = parseInt(newQty) || 0;
      
      const response = await fetch(`${API_URL}/api/update_item_quantity_outbound`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shipmentId,
          boxNumber: currentBox.boxNumber,
          sku: item.SKU,
          quantity,
          selectedNickname
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update quantity');
      }

      // Immediately refresh data
      await Promise.all([
        refreshBoxes(),
        reloadShipmentData && reloadShipmentData()
      ]);
      
      showToast(`Quantity updated to ${quantity}`, 'success');
    } catch (error) {
      console.error('Error updating quantity:', error);
      showToast(`Failed to update quantity: ${error.message}`, 'error');
    }
  };

  const handleRemoveItem = async (index) => {
    try {
      if (index < 0 || index >= currentBox.items.length) {
        showToast('Invalid item index', 'error');
        return;
      }

      const itemToRemove = currentBox.items[index];
      
      const response = await fetch(`${API_URL}/api/update_item_quantity_outbound`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shipmentId,
          boxNumber: currentBox.boxNumber,
          sku: itemToRemove.SKU,
          quantity: 0,
          selectedNickname
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove item');
      }

      showToast(`Removed ${itemToRemove.SKU || 'item'} from box`, 'success');
      await refreshBoxes();
    } catch (error) {
      console.error('Error removing item:', error);
      showToast(`Failed to remove item: ${error.message}`, 'error');
    }
  };

  const handleWeightBlur = async (asin, newValue) => {
    try {
      const response = await fetch(`${API_URL}/api/update_weight`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          asin: asin, 
          weight: newValue,
          employee: selectedNickname
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update weight');
      }

      // Immediately refresh all relevant data
      await Promise.all([
        reloadAsinData && reloadAsinData(),
        refreshBoxes(),
        reloadShipmentData && reloadShipmentData()
      ]);
      
      showToast('Weight updated successfully', 'success');
    } catch (error) {
      console.error('Error updating weight:', error.message);
      showToast('Failed to update weight', 'error');
    }
  };

  const handlePriceBlur = async (asin, newValue) => {
    try {
      const response = await fetch(`${API_URL}/api/update_price`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ asin: asin, value: newValue, employee: selectedNickname })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update price');
      }
      
      // Immediately refresh all relevant data
      await Promise.all([
        reloadAsinData && reloadAsinData(),
        refreshBoxes(),
        reloadShipmentData && reloadShipmentData()
      ]);

      showToast('Price updated successfully', 'success');
    } catch (error) {
      console.error('Error updating price:', error.message);
      showToast('Failed to update price', 'error');
    }
  };

  const updateExpiryDate = async (date, shipmentItem) => {
    const formatDate = (date) => {
      const adjustedDate = new Date(date);
      adjustedDate.setDate(adjustedDate.getDate() + 1);
      adjustedDate.setHours(12, 0, 0, 0);
      return adjustedDate.toISOString();
    };

    const newExpiryDate = date ? formatDate(new Date(date)) : null;
    
    // Get current expiry date for comparison
    const currentExpiryDate = shipmentItem.expiry_date;
    
    // Only proceed if the date is valid and different from current
    if (date && !date.trim()) {
      // Empty string, don't update
      return;
    }
    
    // Compare dates - normalize both to same format for comparison
    const normalizeDate = (dateStr) => {
      if (!dateStr) return null;
      try {
        return new Date(dateStr).toISOString().split('T')[0];
      } catch {
        return null;
      }
    };
    
    const currentNormalized = normalizeDate(currentExpiryDate);
    const newNormalized = date ? normalizeDate(date) : null;
    
    // If dates are the same, don't update
    if (currentNormalized === newNormalized) {
      return;
    }

    const itemId = shipmentItem.id;
    const shipmentId = shipmentItem.shipment_id;

    if (date) {
      const currentDate = new Date();
      const dateObj = new Date(date);
      const differenceInDays = (dateObj - currentDate) / (1000 * 60 * 60 * 24);

      const isOpenMarketplace = clientInfo?.name?.toLowerCase().includes('openmarketplace');
      
      if (isOpenMarketplace && differenceInDays < 160) {
        showToast('For OpenMarketplace, expiry date must be at least 160 days from today.', 'warning');
        return;
      } else if (!isOpenMarketplace && differenceInDays < 90) {
        showToast('Warning: Expiry date is less than 90 days from today.', 'warning');
      }
    }
    
    try {
      const modifiedItem = {
        ...shipmentItem,
        expiry_date: newExpiryDate
      };
      const response = await fetch(`${API_URL}/api/update-expiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          itemId: itemId,
          shipmentId: shipmentId,
          item: modifiedItem,
        }),
      });

      if (response.ok) {
        // Immediately refresh all relevant data
        await Promise.all([
          reloadShipmentData && reloadShipmentData(),
          refreshBoxes(),
          reloadAsinData && reloadAsinData()
        ]);
        
        showToast('Expiry date updated successfully', 'success');
      } else {
        console.error('Failed to update expiry date');
        showToast('Failed to update expiry date', 'error');
      }
    } catch (error) {
      console.error('Error updating expiry date:', error);
      showToast('Error updating expiry date', 'error');
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (e) {
      return '';
    }
  };

  const getBoxPresets = () => {
    if (!availableStandardBoxes || !Array.isArray(availableStandardBoxes) || availableStandardBoxes.length === 0) {
      return [];
    }
    
    return availableStandardBoxes.map(box => ({
      name: box.NAME,
      length: box.LENGTH,
      width: box.WIDTH,
      height: box.HEIGHT,
      weight: dims.weight
    }));
  };

  // Calculate total box value
  const calculateBoxValue = () => {
    if (!currentBox.items || currentBox.items.length === 0) {
      return 0;
    }
    
    let total = 0;
    let hasAnyValue = false;
    
    currentBox.items.forEach(item => {
      let itemValue = null;
      
      if (effectiveAsinData && effectiveAsinData.length > 0) {
        const asinDataItem = effectiveAsinData.find(data => data.ASIN === item.ASIN);
        if (asinDataItem && asinDataItem.VALUE) {
          itemValue = parseFloat(asinDataItem.VALUE);
        }
      }
      
      if (itemValue && !isNaN(itemValue)) {
        total += itemValue * parseInt(item.QTY || 0);
        hasAnyValue = true;
      }
    });
    
    return hasAnyValue ? total : 0;
  };

  const boxValue = calculateBoxValue();
  const isOverLimit = boxValue > 800;
  const hasValueData = boxValue > 0;
  const isUSShipment = clientCountry && (clientCountry.toUpperCase() === 'US' || clientCountry.toUpperCase() === 'USA' || clientCountry.toUpperCase() === 'UNITED STATES');

  // Calculate total allocated units per item across all boxes
  const calculateAllocatedUnits = (asin, sku) => {
    if (!boxes || !Array.isArray(boxes)) return 0;
    
    let totalAllocated = 0;
    boxes.forEach(box => {
      if (box.items && Array.isArray(box.items)) {
        box.items.forEach(item => {
          // Match by ASIN or SKU
          if (item.ASIN === asin || item.SKU === sku) {
            totalAllocated += parseInt(item.QTY || 0);
          }
        });
      }
    });
    
    return totalAllocated;
  };

  // Check if all units for an item are allocated
  const isItemFullyAllocated = (asin, sku) => {
    const shipmentItem = shipmentItems?.find(si => 
      si.item?.asin === asin || 
      si.item?.merchant_sku === sku
    );
    
    if (!shipmentItem) return false;
    
    const totalAvailable = parseInt(shipmentItem.quantity || 0);
    const totalAllocated = calculateAllocatedUnits(asin, sku);
    
    return totalAllocated >= totalAvailable;
  };

  const handleAddPreset = async () => {
    if (!newPreset.name.trim() || !newPreset.length || !newPreset.width || !newPreset.height) {
      showToast('Please fill in all preset fields', 'error');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/add_standard_box`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          NAME: newPreset.name.trim(),
          LENGTH: parseInt(newPreset.length),
          WIDTH: parseInt(newPreset.width),
          HEIGHT: parseInt(newPreset.height),
          CHARGE: newPreset.chargeable ? 'YES' : 'NO',
          employee: selectedNickname
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add preset');
      }

      showToast('Preset box added successfully', 'success');
      
      // Reset form and hide it
      setNewPreset({ name: '', length: '', width: '', height: '', chargeable: false });
      setShowAddPreset(false);
      
      // Refresh standard boxes
      if (fetchStandardBoxes) {
        await fetchStandardBoxes();
      }
      if (refreshStandardBoxes) {
        await refreshStandardBoxes();
      }
    } catch (error) {
      console.error('Error adding preset:', error);
      showToast(`Failed to add preset: ${error.message}`, 'error');
    }
  };

                return (
     <div className="bg-white shadow-sm border border-slate-200 rounded-lg mx-3 mb-3">
       <div className="p-6 space-y-8">
         {/* Box Configuration Section */}
         <div className="space-y-4">
           


           {/* Professional Dimensions Input */}
           <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
             <div className={`grid ${isLTL ? 'grid-cols-5' : 'grid-cols-4'} gap-4`}>
               <div className="space-y-2">
                 <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Length</label>
                 <div className="relative">
                   <Input 
                     type="number" 
                     value={parseInt(dims.length) || ''} 
                     onChange={e => {
                       const value = e.target.value;
                       const roundedValue = value === '' ? '' : Math.round(parseFloat(value) || 0).toString();
                       setDims({...dims, length: roundedValue});
                     }} 
                     onBlur={e => {
                       const value = e.target.value === '' ? '' : Math.round(parseFloat(e.target.value) || 0).toString();
                       const originalValue = parseInt(currentBox.length) || 0;
                       const newValue = parseInt(value) || 0;
                       
                       // Only save if value changed
                       if (newValue !== originalValue) {
                         handleDimBlur('length', value);
                       }
                       // Clear focus state after blur
                       setFocusedFields(prev => ({ ...prev, length: false }));
                     }}
                     onFocus={(e) => {
                       e.target.select();
                       setFocusedFields(prev => ({ ...prev, length: true }));
                     }}
                     className="h-10 text-center border-slate-300 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500" 
                     placeholder="0" 
                     step="1"
                     min="0"
                   />
                   <div className="absolute right-3 top-2.5 text-xs text-slate-500">cm</div>
                   {dimsSaving.length && <Check className="absolute right-8 top-2.5 w-4 h-4 text-green-600" />}
                 </div>
               </div>
               
               <div className="space-y-2">
                 <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Width</label>
                 <div className="relative">
                   <Input 
                     type="number" 
                     value={parseInt(dims.width) || ''} 
                     onChange={e => {
                       const value = e.target.value;
                       const roundedValue = value === '' ? '' : Math.round(parseFloat(value) || 0).toString();
                       setDims({...dims, width: roundedValue});
                     }} 
                     onBlur={e => {
                       const value = e.target.value === '' ? '' : Math.round(parseFloat(e.target.value) || 0).toString();
                       const originalValue = parseInt(currentBox.width) || 0;
                       const newValue = parseInt(value) || 0;
                       
                       // Only save if value changed
                       if (newValue !== originalValue) {
                         handleDimBlur('width', value);
                       }
                       // Clear focus state after blur
                       setFocusedFields(prev => ({ ...prev, width: false }));
                     }}
                     onFocus={(e) => {
                       e.target.select();
                       setFocusedFields(prev => ({ ...prev, width: true }));
                     }}
                     className="h-10 text-center border-slate-300 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500" 
                     placeholder="0" 
                     step="1"
                     min="0"
                   />
                   <div className="absolute right-3 top-2.5 text-xs text-slate-500">cm</div>
                   {dimsSaving.width && <Check className="absolute right-8 top-2.5 w-4 h-4 text-green-600" />}
                 </div>
               </div>
               
               <div className="space-y-2">
                 <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Height</label>
                 <div className="relative">
                   <Input 
                     type="number" 
                     value={parseInt(dims.height) || ''} 
                     onChange={e => {
                       const value = e.target.value;
                       const roundedValue = value === '' ? '' : Math.round(parseFloat(value) || 0).toString();
                       setDims({...dims, height: roundedValue});
                     }} 
                     onBlur={e => {
                       const value = e.target.value === '' ? '' : Math.round(parseFloat(e.target.value) || 0).toString();
                       const originalValue = parseInt(currentBox.height) || 0;
                       const newValue = parseInt(value) || 0;
                       
                       // Only save if value changed
                       if (newValue !== originalValue) {
                         handleDimBlur('height', value);
                       }
                       // Clear focus state after blur
                       setFocusedFields(prev => ({ ...prev, height: false }));
                     }}
                     onFocus={(e) => {
                       e.target.select();
                       setFocusedFields(prev => ({ ...prev, height: true }));
                     }}
                     className="h-10 text-center border-slate-300 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500" 
                     placeholder="0" 
                     step="1"
                     min="0"
                   />
                   <div className="absolute right-3 top-2.5 text-xs text-slate-500">cm</div>
                   {dimsSaving.height && <Check className="absolute right-8 top-2.5 w-4 h-4 text-green-600" />}
                 </div>
               </div>
               
               <div className="space-y-2">
                 <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Weight</label>
                 <div className="relative">
                   <Input 
                     type="number" 
                     value={dims.weight} 
                     onChange={e => {
                       const value = e.target.value;
                       const roundedValue = value === '' ? '' : (Math.round((parseFloat(value) || 0) * 100) / 100).toString();
                       setDims({...dims, weight: roundedValue});
                     }} 
                     onBlur={e => {
                       const value = e.target.value === '' ? '' : (Math.round((parseFloat(e.target.value) || 0) * 100) / 100).toString();
                       const originalValue = parseFloat(currentBox.weight) || 0;
                       const newValue = parseFloat(value) || 0;
                       
                       // Only save if value changed (with small tolerance for floating point comparison)
                       if (Math.abs(newValue - originalValue) > 0.001) {
                         handleDimBlur('weight', value);
                       }
                       // Clear focus state after blur
                       setFocusedFields(prev => ({ ...prev, weight: false }));
                     }}
                     onFocus={(e) => {
                       e.target.select();
                       setFocusedFields(prev => ({ ...prev, weight: true }));
                     }}
                     className={`h-10 text-center bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
                       parseFloat(dims.weight) > 21 
                         ? 'border-red-500 bg-red-50' 
                         : 'border-slate-300'
                     }`} 
                     placeholder="0.00" 
                     step="0.01"
                     min="0"
                   />
                   <div className="absolute right-3 top-2.5 text-xs text-slate-500">kg</div>
                   {dimsSaving.weight && <Check className="absolute right-8 top-2.5 w-4 h-4 text-green-600" />}
                 </div>
               </div>

               {/* Pallet field - only shown if LTL mode is enabled */}
               {isLTL && (
                 <div className="space-y-2">
                   <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Pallet</label>
                   <div className="relative">
                     <Input 
                       type="number" 
                       value={palletValue} 
                       onChange={(e) => {
                         setPalletValue(e.target.value);
                       }}
                       onBlur={async (e) => {
                         const value = e.target.value;
                         if (value && parseInt(value) > 0 && value !== (currentBox.PALLET || '1')) {
                           await onPalletChange(box.boxNumber, value);
                         }
                       }}
                       onKeyPress={(e) => {
                         if (e.key === 'Enter') {
                           e.target.blur();
                         }
                       }}
                       className="h-10 text-center border-slate-300 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500" 
                       placeholder="1" 
                       min="1"
                       max="99"
                     />
                   </div>
                 </div>
               )}
             </div>

             {/* Second Row for Presets and Weight Analysis */}
             <div className="grid grid-cols-4 gap-4 mt-2">
               {/* Preset Box Selection - Spans first 3 columns */}
               <div className="col-span-3">
                 <div className="flex items-center gap-2">
                   <div className="relative flex-1">
                     <button
                       type="button"
                       onClick={() => setShowPresetSelect(!showPresetSelect)}
                       className="w-full h-8 bg-white border border-slate-300 hover:border-slate-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm px-3 flex items-center justify-between transition-colors"
                     >
                       <span className={selectedPreset ? 'text-slate-900' : 'text-slate-500'}>
                         {selectedPreset || 'Select preset...'}
                       </span>
                       <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showPresetSelect ? 'rotate-180' : ''}`} />
                     </button>
                     
                     {showPresetSelect && (
                       <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-300 shadow-lg z-50 max-h-60 overflow-y-auto min-w-80">
                         <div 
                           className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 cursor-pointer transition-colors"
                           onClick={async () => {
                             setSelectedPreset(null);
                             setShowPresetSelect(false);
                           }}
                         >
                           <span className="text-slate-400">Clear selection</span>
                         </div>
                         {availableStandardBoxes.map((preset, idx) => (
                           <div
                             key={idx}
                             className="px-4 py-3 text-sm hover:bg-blue-50 cursor-pointer transition-colors border-t border-slate-100"
                             onClick={async () => {
                               const newDims = { length: preset.LENGTH, width: preset.WIDTH, height: preset.HEIGHT, weight: dims.weight };
                               setDims(newDims);
                               setSelectedPreset(preset.NAME);
                               setShowPresetSelect(false);
                               
                               try {
                                 setDimsSaving({ length: true, width: true, height: true, weight: false });
                                 const updated = { 
                                   ...currentBox, 
                                   length: preset.LENGTH, 
                                   width: preset.WIDTH, 
                                   height: preset.HEIGHT 
                                 };
                                 await onUpdateBoxDetails(updated);
                                 await refreshBoxes();
                                 showToast(`Applied ${preset.NAME}`, 'success');
                                 
                                 setTimeout(() => {
                                   setDimsSaving({ length: false, width: false, height: false, weight: false });
                                 }, 1000);
                               } catch (error) {
                                 console.error('Error updating preset dimensions:', error);
                                 showToast('Failed to apply preset', 'error');
                                 setDimsSaving({ length: false, width: false, height: false, weight: false });
                               }
                             }}
                           >
                             <div className="flex items-center justify-between">
                               <div className="flex items-center gap-2">
                                 <span className="font-medium text-slate-900">{preset.NAME}</span>
                                 <span className="text-slate-500">
                                   {preset.LENGTH}×{preset.WIDTH}×{preset.HEIGHT} cm
                                 </span>
                               </div>
                               {preset.CHARGEABLE && (
                                 <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 font-medium">
                                   Chargeable
                                 </span>
                               )}
                             </div>
                           </div>
                         ))}
                       </div>
                     )}
                   </div>
                   
                   <Button
                     onClick={() => setShowAddPreset(true)}
                     variant="outline"
                     size="sm"
                     className="h-7 px-2 text-xs bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
                   >
                     <Plus className="w-3 h-3" />
                   </Button>
                 </div>
               </div>
               
               {/* Weight Analysis - Spans 4th column */}
               <div className="col-span-1">
                 {(() => {
                   const autoWeightResult = calcAutoWeight();
                   const currentWeight = parseFloat(dims.weight) || 0;
                   const calculatedWeight = parseFloat(autoWeightResult.weight) || 0;
                   const weightDifference = Math.abs(currentWeight - calculatedWeight);
                   const hasWeightMismatch = autoWeightResult.canCalculate && currentWeight > 0 && weightDifference > 0.1;
                   const isOverLimit = parseFloat(dims.weight) > 21;
                   
                   const showWeightAnalysis = autoWeightResult.canCalculate || isOverLimit || hasWeightMismatch;
                   
                   return showWeightAnalysis && (
                     <div className="space-y-1">
                       {/* Calculated Weight Display */}
                       {autoWeightResult.canCalculate && (
                         <div className="flex items-center justify-between gap-2">
                           <span className="text-xs text-slate-600">
                             Calc: {calculatedWeight.toFixed(2)}kg
                           </span>
                           <Button 
                             variant="outline"
                             size="sm"
                             className="h-6 px-2 text-xs bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                             onClick={async () => { 
                               const w = autoWeightResult.weight; 
                               if (w) {
                                 setDims({...dims, weight: w});
                                 try {
                                   setDimsSaving(prev => ({ ...prev, weight: true }));
                                   const updated = { ...currentBox, weight: w };
                                   await onUpdateBoxDetails(updated);
                                   refreshBoxes();
                                   setTimeout(() => {
                                     setDimsSaving(prev => ({ ...prev, weight: false }));
                                   }, 1000);
                                 } catch (error) {
                                   console.error('Error updating calculated weight:', error);
                                   showToast('Failed to update weight', 'error');
                                   setDimsSaving(prev => ({ ...prev, weight: false }));
                                 }
                               }
                             }}
                           >
                             <Calculator className="w-3 h-3 mr-1" />
                             Use
                           </Button>
                         </div>
                       )}
                       
                       {/* Weight Warnings */}
                       {isOverLimit && (
                         <div className="text-xs text-red-600 flex items-center gap-1">
                           <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                           <span>Over 21kg limit</span>
                         </div>
                       )}
                       
                       {hasWeightMismatch && (
                         <div className="text-xs text-amber-600 flex items-center gap-1">
                           <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                           <span>Weight mismatch</span>
                         </div>
                       )}
                     </div>
                   );
                 })()}
               </div>
             </div>
             

           </div>
         </div>

         {/* Items Section */}
         <div className="space-y-4">
           <div className="bg-slate-50 rounded-lg border border-slate-200">
             {/* Add Item Header */}
             <div className="p-3 border-b border-slate-200">
               <AddItem
                 selectedNickname={selectedNickname}
                 selectedPlan={selectedPlan || { id: shipmentId }}
                 shipmentItems={shipmentItems}
                 onAddItem={async (newItem) => {
                   try {
                     await Promise.all([
                       refreshBoxes(),
                       reloadShipmentData && reloadShipmentData(),
                       reloadAsinData && reloadAsinData()
                     ]);
                     
                     showToast('Item added successfully', 'success');
                   } catch (error) {
                     console.error('Error adding item:', error);
                     showToast('Failed to add item', 'error');
                   }
                 }}
                 boxNumber={currentBox.boxNumber}
                 length={dims.length}
                 width={dims.width}
                 height={dims.height}
                 weight={dims.weight}
                 shipmentId={shipmentId}
               />
             </div>
             
             {/* Items Table */}
             {currentBox.items.length > 0 ? (
               <div className="overflow-x-auto">
                 <table className="w-full text-xs">
                   <thead className="bg-slate-100">
                     <tr>
                       <th className="text-left p-2 font-medium text-slate-600 w-8"></th>
                       <th className="text-left p-2 font-medium text-slate-600">Product</th>
                       <th className="text-center p-2 font-medium text-slate-600 w-16">Qty</th>
                       <th className="text-center p-2 font-medium text-slate-600 w-20">Weight</th>
                       {isUSShipment && <th className="text-center p-2 font-medium text-slate-600 w-20">Price</th>}
                       <th className="text-center p-2 font-medium text-slate-600 w-24">Expiry</th>
                       <th className="text-center p-2 font-medium text-slate-600 w-8"></th>
                     </tr>
                   </thead>
                   <tbody className="bg-white">
                     {currentBox.items.map((item, index) => {
                       const asinInfo = getAsinData(item.ASIN);
                       const { title, image, weight, fnsku, sku, value } = getProductInfo(item, asinInfo);
                       const shipmentItem = shipmentItems?.find(si =>
                         si.item?.asin === item.ASIN ||
                         si.item?.merchant_sku === item.SKU ||
                         si.item?.fnsku === item.FNSKU
                       );
                       const expiryDate = shipmentItem?.expiry_date || item.EXPIRY;
                       const isFullyAllocated = isItemFullyAllocated(item.ASIN, item.SKU);
                       const isJustScanned = lastScannedItemSku === item.SKU;

                       return (
                         <tr key={index} className={`border-b border-slate-200 ${
                           isJustScanned
                             ? 'animate-yellow-flash'
                             : isFullyAllocated
                               ? 'bg-green-50'
                               : 'hover:bg-slate-50'
                         }`}>
                           <td className="p-2">
                             <div className="w-8 h-8 bg-slate-50 border border-slate-200 overflow-hidden flex items-center justify-center">
                               {image ? (
                                 <img src={image} alt={title} className="w-full h-full object-cover" />
                               ) : (
                                 <Package className="w-4 h-4 text-slate-400" />
                               )}
                             </div>
                           </td>
                           <td className="p-2">
                             <div className="font-medium text-slate-900 truncate max-w-48" title={title}>
                               {title}
                             </div>
                             <div className="text-slate-500 text-xs truncate">{sku}</div>
                             <div className="text-slate-400 text-xs truncate">{item.ASIN}</div>
                             <div className="text-slate-400 text-xs truncate">{fnsku}</div>
                           </td>
                           <td className="p-2 text-center">
                             {editingItemField[`${index}-qty`] ? (
                               <Input
                                 type="number"
                                 value={editingValues[`${index}-qty`] ?? item.QTY}
                                 onChange={(e) => {
                                   const value = e.target.value;
                                   // Round to integer for quantity
                                   const roundedValue = value === '' ? '' : Math.round(parseFloat(value) || 0).toString();
                                   setEditingValues(prev => ({ ...prev, [`${index}-qty`]: roundedValue }));
                                 }}
                                 onBlur={async (e) => {
                                   const value = e.target.value === '' ? '0' : Math.round(parseFloat(e.target.value) || 0).toString();
                                   const originalValue = parseInt(item.QTY) || 0;
                                   const newValue = parseInt(value) || 0;
                                   
                                   // Only save if value changed
                                   if (newValue !== originalValue) {
                                     await handleQuantityChange(index, value);
                                   }
                                   
                                   setEditingItemField(prev => ({ ...prev, [`${index}-qty`]: false }));
                                   setEditingValues(prev => ({ ...prev, [`${index}-qty`]: undefined }));
                                 }}
                                 onKeyPress={async (e) => {
                                   if (e.key === 'Enter') {
                                     const value = e.target.value === '' ? '0' : Math.round(parseFloat(e.target.value) || 0).toString();
                                     const originalValue = parseInt(item.QTY) || 0;
                                     const newValue = parseInt(value) || 0;
                                     
                                     // Only save if value changed
                                     if (newValue !== originalValue) {
                                       await handleQuantityChange(index, value);
                                     }
                                     
                                     setEditingItemField(prev => ({ ...prev, [`${index}-qty`]: false }));
                                     setEditingValues(prev => ({ ...prev, [`${index}-qty`]: undefined }));
                                   }
                                 }}
                                 onFocus={(e) => e.target.select()}
                                 className="h-6 w-12 text-center text-xs"
                                 min="1"
                                 step="1"
                                 autoFocus
                               />
                             ) : (
                               <div 
                                 onClick={() => {
                                   setEditingItemField(prev => ({ ...prev, [`${index}-qty`]: true }));
                                   setEditingValues(prev => ({ ...prev, [`${index}-qty`]: parseInt(item.QTY) || 0 }));
                                 }}
                                 className="h-6 px-2 border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors flex items-center justify-center font-medium"
                               >
                                 {parseInt(item.QTY) || 0}
                               </div>
                             )}
                           </td>
                           <td className="p-2 text-center">
                             {editingItemField[`${index}-weight`] ? (
                               <Input
                                 type="number"
                                 value={editingValues[`${index}-weight`] ?? (weight || '')}
                                 onChange={(e) => {
                                   const value = e.target.value;
                                   // Round to 2 decimal places for weight
                                   const roundedValue = value === '' ? '' : (Math.round((parseFloat(value) || 0) * 100) / 100).toString();
                                   setEditingValues(prev => ({ ...prev, [`${index}-weight`]: roundedValue }));
                                 }}
                                 onBlur={async (e) => {
                                   const value = e.target.value === '' ? '0' : (Math.round((parseFloat(e.target.value) || 0) * 100) / 100).toString();
                                   const originalValue = parseFloat(weight || 0);
                                   const newValue = parseFloat(value) || 0;
                                   
                                   // Only save if value changed (with small tolerance for floating point comparison)
                                   if (Math.abs(newValue - originalValue) > 0.001) {
                                     await handleWeightBlur(item.ASIN, value);
                                   }
                                   
                                   setEditingItemField(prev => ({ ...prev, [`${index}-weight`]: false }));
                                   setEditingValues(prev => ({ ...prev, [`${index}-weight`]: undefined }));
                                 }}
                                 onKeyPress={async (e) => {
                                   if (e.key === 'Enter') {
                                     const value = e.target.value === '' ? '0' : (Math.round((parseFloat(e.target.value) || 0) * 100) / 100).toString();
                                     const originalValue = parseFloat(weight || 0);
                                     const newValue = parseFloat(value) || 0;
                                     
                                     // Only save if value changed (with small tolerance for floating point comparison)
                                     if (Math.abs(newValue - originalValue) > 0.001) {
                                       await handleWeightBlur(item.ASIN, value);
                                     }
                                     
                                     setEditingItemField(prev => ({ ...prev, [`${index}-weight`]: false }));
                                     setEditingValues(prev => ({ ...prev, [`${index}-weight`]: undefined }));
                                   }
                                 }}
                                 onFocus={(e) => e.target.select()}
                                 className="h-6 w-16 text-center text-xs"
                                 step="0.01"
                                 min="0"
                                 autoFocus
                               />
                             ) : (
                               <div 
                                 onClick={canEditDetails ? () => {
                                   setEditingItemField(prev => ({ ...prev, [`${index}-weight`]: true }));
                                   setEditingValues(prev => ({ ...prev, [`${index}-weight`]: (parseFloat(weight || 0)).toFixed(2) }));
                                 } : undefined}
                                 className={`h-6 px-2 border border-slate-200 ${canEditDetails ? 'bg-slate-50 hover:bg-slate-100 cursor-pointer' : 'bg-gray-100 cursor-not-allowed'} transition-colors flex items-center justify-center font-medium`}
                               >
                                 {parseFloat(weight || 0).toFixed(2)}
                               </div>
                             )}
                           </td>
                           {isUSShipment && (
                             <td className="p-2 text-center">
                               {editingItemField[`${index}-price`] ? (
                                 <Input
                                   type="number"
                                   value={editingValues[`${index}-price`] ?? (value || '')}
                                   onChange={(e) => {
                                     const inputValue = e.target.value;
                                     // Round to 2 decimal places for price
                                     const roundedValue = inputValue === '' ? '' : (Math.round((parseFloat(inputValue) || 0) * 100) / 100).toString();
                                     setEditingValues(prev => ({ ...prev, [`${index}-price`]: roundedValue }));
                                   }}
                                   onBlur={async (e) => {
                                     const inputValue = e.target.value === '' ? '0' : (Math.round((parseFloat(e.target.value) || 0) * 100) / 100).toString();
                                     const originalValue = parseFloat(value || 0);
                                     const newValue = parseFloat(inputValue) || 0;
                                     
                                     // Only save if value changed (with small tolerance for floating point comparison)
                                     if (Math.abs(newValue - originalValue) > 0.001) {
                                       await handlePriceBlur(item.ASIN, inputValue);
                                     }
                                     
                                     setEditingItemField(prev => ({ ...prev, [`${index}-price`]: false }));
                                     setEditingValues(prev => ({ ...prev, [`${index}-price`]: undefined }));
                                   }}
                                   onKeyPress={async (e) => {
                                     if (e.key === 'Enter') {
                                       const inputValue = e.target.value === '' ? '0' : (Math.round((parseFloat(e.target.value) || 0) * 100) / 100).toString();
                                       const originalValue = parseFloat(value || 0);
                                       const newValue = parseFloat(inputValue) || 0;
                                       
                                       // Only save if value changed (with small tolerance for floating point comparison)
                                       if (Math.abs(newValue - originalValue) > 0.001) {
                                         await handlePriceBlur(item.ASIN, inputValue);
                                       }
                                       
                                       setEditingItemField(prev => ({ ...prev, [`${index}-price`]: false }));
                                       setEditingValues(prev => ({ ...prev, [`${index}-price`]: undefined }));
                                     }
                                   }}
                                   onFocus={(e) => e.target.select()}
                                   className="h-6 w-16 text-center text-xs"
                                   step="0.01"
                                   min="0"
                                   autoFocus
                                 />
                               ) : (
                                 <div 
                                   onClick={canEditDetails ? () => {
                                     setEditingItemField(prev => ({ ...prev, [`${index}-price`]: true }));
                                     setEditingValues(prev => ({ ...prev, [`${index}-price`]: (parseFloat(value || 0)).toFixed(2) }));
                                   } : undefined}
                                   className={`h-6 px-2 border border-slate-200 ${canEditDetails ? 'bg-slate-50 hover:bg-slate-100 cursor-pointer' : 'bg-gray-100 cursor-not-allowed'} transition-colors flex items-center justify-center font-medium`}
                                 >
                                   ${parseFloat(value || 0).toFixed(2)}
                                 </div>
                               )}
                             </td>
                           )}
                           <td className="p-2 text-center">
                             {editingItemField[`${index}-expiry`] ? (
                               <ExpirationDatePicker
                                 value={formatDateForInput(expiryDate)}
                                 onChange={async (newDate) => {
                                   if (shipmentItem) {
                                     await updateExpiryDate(newDate, shipmentItem);
                                   }
                                   setEditingItemField(prev => ({ ...prev, [`${index}-expiry`]: false }));
                                 }}
                                 onBlur={() => setEditingItemField(prev => ({ ...prev, [`${index}-expiry`]: false }))}
                                 className="h-6 w-20 text-center text-xs"
                                 autoFocus
                               />
                             ) : (
                               <div 
                                 onClick={canEditDetails ? () => {
                                   setEditingItemField(prev => ({ ...prev, [`${index}-expiry`]: true }));
                                 } : undefined}
                                 className={`h-6 px-2 border border-slate-200 ${canEditDetails ? 'bg-slate-50 hover:bg-slate-100 cursor-pointer' : 'bg-gray-100 cursor-not-allowed'} transition-colors flex items-center justify-center font-medium`}
                               >
                                 {expiryDate ? new Date(expiryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Not set'}
                               </div>
                             )}
                           </td>
                           <td className="p-2 text-center">
                             <Button
                               variant="ghost"
                               size="sm"
                               onClick={() => handleRemoveItem(index)}
                               className="h-6 w-6 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
                             >
                               <X className="w-3 h-3" />
                             </Button>
                           </td>
                         </tr>
                       );
                     })}
                   </tbody>
                   </table>
                 </div>
               ) : (
                 <div className="p-8 text-center">
                   <div className="text-slate-400 text-sm">No items in this box</div>
                   <div className="text-xs text-slate-400 mt-1">Add items using the form above</div>
                 </div>
               )}
             </div>
           </div>

         {/* Box Actions */}
         <div className="space-y-4">
           
           <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
             <div className="flex items-center justify-between gap-4">
               <div className="flex items-center gap-2">
                 <Input 
                   type="number" 
                   value={duplicateCount} 
                   onChange={e => setDuplicateCount(Math.max(1, parseInt(e.target.value) || 1))} 
                   onFocus={(e) => e.target.select()}
                   className="h-8 w-16 text-center border-slate-300 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500" 
                   min="1"
                   step="1"
                   placeholder="1"
                 />
                 <Button 
                   onClick={handleDuplicate} 
                   variant="outline" 
                   size="sm"
                   className="h-8 bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 focus:ring-1 focus:ring-blue-500"
                 >
                   <Copy className="w-3 h-3 mr-1" />
                   Duplicate
                 </Button>
               </div>
               
               <Button 
                 onClick={onDeleteBox} 
                 variant="outline"
                 size="sm"
                 className="h-8 bg-red-50 border-red-300 text-red-700 hover:bg-red-100 focus:ring-1 focus:ring-red-500"
               >
                 <Trash2 className="w-3 h-3 mr-1" />
                 Delete
               </Button>
             </div>
           </div>
         </div>
      

        {/* Add Preset Modal */}
        <Dialog open={showAddPreset} onOpenChange={setShowAddPreset}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Preset Box</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Box Name</label>
                <Input
                  placeholder="Enter box name"
                  value={newPreset.name}
                  onChange={e => setNewPreset(prev => ({ ...prev, name: e.target.value }))}
                  onFocus={(e) => e.target.select()}
                  className="h-9"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Length (cm)</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={newPreset.length}
                    onChange={e => setNewPreset(prev => ({ ...prev, length: e.target.value }))}
                    onFocus={(e) => e.target.select()}
                    className="h-9 text-center"
                    min="0"
                    step="1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Width (cm)</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={newPreset.width}
                    onChange={e => setNewPreset(prev => ({ ...prev, width: e.target.value }))}
                    onFocus={(e) => e.target.select()}
                    className="h-9 text-center"
                    min="0"
                    step="1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Height (cm)</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={newPreset.height}
                    onChange={e => setNewPreset(prev => ({ ...prev, height: e.target.value }))}
                    onFocus={(e) => e.target.select()}
                    className="h-9 text-center"
                    min="0"
                    step="1"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  id="newPresetChargeable"
                  type="checkbox"
                  checked={newPreset.chargeable}
                  onChange={(e) => setNewPreset(prev => ({ ...prev, chargeable: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="newPresetChargeable" className="text-sm font-medium text-slate-700 cursor-pointer">
                  This is a chargeable box
                </label>
              </div>
            </div>
            <DialogFooter className="flex justify-end gap-2 pt-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAddPreset(false);
                  setNewPreset({ name: '', length: '', width: '', height: '', chargeable: false });
                }}
                className="h-9 px-4"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddPreset}
                className="h-9 px-4 bg-blue-600 hover:bg-blue-700"
                disabled={!newPreset.name.trim() || !newPreset.length || !newPreset.width || !newPreset.height}
              >
                Add Preset
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      
      </div>
    </div>
  );
};

const AddBoxPlaceholder = ({ onClick }) => {
  return (
    <div
      className="w-full h-12 flex items-center justify-center border-2 border-dashed border-slate-400 cursor-pointer transition-colors duration-150 bg-slate-50 hover:bg-blue-50 hover:border-blue-400 px-3"
      onClick={onClick}
    >
      <div className="flex items-center gap-2">
        <div className="text-2xl text-slate-400">+</div>
        <div className="text-sm text-slate-600 font-medium">
          Add Box
        </div>
      </div>
    </div>
  );
};

const BoxesPanel = ({
  boxes,
  onAddBox,
  onDuplicateBox,
  onUpdateBoxDetails,
  shipmentItems,
  asinData,
  selectedNickname,
  clientCountry,
  standardBoxes,
  fetchStandardBoxes,
  shipmentId,
  autoPrintEnabled,
  selectedDefaultPrinter,
  canEditItemDetails,
  currentRole,
  onBoxSelect,
  disableScanner = false,
}) => {
  const { setShipmentItems, refreshBoxes, selectedPlan, asinData: contextAsinData, reloadAsinData, reloadShipmentData } = useContext(BoxContext);
  const { toast: uiToast } = useToast();

  // Use the same effective ASIN data logic as BoxSidebar
  const effectiveAsinData = asinData && asinData.length > 0 ? asinData : (contextAsinData || []);

  // Determine if current user can edit item details (weight, price, expiry)
  const canEditDetails = currentRole !== COORDINATOR_ROLES.BOX_CONTENTS;

  const [selectedBoxNumber, setSelectedBoxNumberState] = useState(null);
  const [hoverPreview, setHoverPreview] = useState(null);

  // Wrapper to update local state and notify parent
  const setSelectedBoxNumber = (boxNumber) => {
    const newBoxNumber = typeof boxNumber === 'function' ? boxNumber(selectedBoxNumber) : boxNumber;
    setSelectedBoxNumberState(newBoxNumber);
    // Notify parent component (for Book Mode)
    if (onBoxSelect) {
      const selectedBox = newBoxNumber ? boxes.find(b => b.boxNumber === newBoxNumber) : null;
      onBoxSelect(newBoxNumber, selectedBox);
    }
  };
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' for A-Z, 'desc' for Z-A
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLTL, setIsLTL] = useState(false);
  const [loadingLTL, setLoadingLTL] = useState(true);
  const [collapsedPallets, setCollapsedPallets] = useState(new Set());
  const [showPalletOverview, setShowPalletOverview] = useState(false);

  // Barcode scanner state
  const [scannerEnabled, setScannerEnabled] = useState(false);
  const [showScanOverlay, setShowScanOverlay] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [pendingScan, setPendingScan] = useState(null);
  const [lastScannedItemSku, setLastScannedItemSku] = useState(null);
  const [overlayFlash, setOverlayFlash] = useState(false);
  const [isProcessingWeight, setIsProcessingWeight] = useState(false);
  const dismissTimerRef = React.useRef(null);

  // Track optimistic quantities to prevent race conditions during rapid scanning
  const optimisticQuantitiesRef = React.useRef({});
  const scanQueueRef = React.useRef([]);
  const isProcessingRef = React.useRef(false);

  // Sound effects - using native Audio API for 100% reliability during rapid scanning
  // Creates NEW audio instance each time = guaranteed playback even at high speed
  const playSuccess = React.useCallback(() => {
    try {
      const audio = new Audio('/sounds/success-scan.mp3');
      audio.volume = 0.7;
      audio.play().catch(err => console.log('Audio play prevented by browser:', err));
    } catch (err) {
      console.log('Sound file not found:', err.message);
    }
  }, []);

  const playError = React.useCallback(() => {
    try {
      const audio = new Audio('/sounds/error-scan.mp3');
      audio.volume = 0.7;
      audio.play().catch(err => console.log('Audio play prevented by browser:', err));
    } catch (err) {
      console.log('Sound file not found:', err.message);
    }
  }, []);

  const playWarning = React.useCallback(() => {
    try {
      const audio = new Audio('/sounds/weight-warning.mp3');
      audio.volume = 0.7;
      audio.play().catch(err => console.log('Audio play prevented by browser:', err));
    } catch (err) {
      console.log('Sound file not found:', err.message);
    }
  }, []);

  // Fetch LTL status on mount
  useEffect(() => {
    const fetchLTLStatus = async () => {
      if (!shipmentId) return;
      
      try {
        const response = await fetch(`${API_URL}/api/outbound-ltl/${shipmentId}`);
        if (response.ok) {
          const data = await response.json();
          setIsLTL(data.isLTL);
        }
      } catch (error) {
        console.error('Error fetching LTL status:', error);
      } finally {
        setLoadingLTL(false);
      }
    };

    fetchLTLStatus();
  }, [shipmentId]);

  // Handle LTL toggle
  const handleLTLToggle = async (newValue) => {
    try {
      const response = await fetch(`${API_URL}/api/outbound-ltl`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outboundId: shipmentId,
          isLTL: newValue
        })
      });

      if (response.ok) {
        setIsLTL(newValue);
        toast({
          title: newValue ? "LTL Mode Enabled" : "LTL Mode Disabled",
          description: newValue ? "All boxes will be assigned to pallets" : "Pallet assignments cleared",
        });
        // Refresh boxes to show updated pallet assignments
        if (refreshBoxes) {
          await refreshBoxes();
        }
      } else {
        throw new Error('Failed to update LTL status');
      }
    } catch (error) {
      console.error('Error updating LTL status:', error);
      toast({
        title: "Error",
        description: "Failed to update LTL status",
        variant: "destructive",
      });
    }
  };

  // Handle pallet change for a box
  const handlePalletChange = async (boxNumber, newPallet) => {
    try {
      const response = await fetch(`${API_URL}/api/update-box-pallet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outboundId: shipmentId,
          boxNumber,
          pallet: newPallet
        })
      });

      if (response.ok) {
        toast({
          title: "Pallet Updated",
          description: `Box #${boxNumber} assigned to pallet ${newPallet}`,
        });
        
        // Close the current accordion to force refresh with new data
        const currentlySelected = selectedBoxNumber;
        setSelectedBoxNumber(null);
        
        if (refreshBoxes) {
          await refreshBoxes();
        }
        
        // Reopen the accordion after a brief delay to ensure data is refreshed
        setTimeout(() => {
          setSelectedBoxNumber(currentlySelected);
        }, 100);
      } else {
        throw new Error('Failed to update pallet');
      }
    } catch (error) {
      console.error('Error updating pallet:', error);
      toast({
        title: "Error",
        description: "Failed to update pallet assignment",
        variant: "destructive",
      });
    }
  };

  // Toggle pallet collapse/expand
  const togglePalletCollapse = (palletNum) => {
    setCollapsedPallets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(palletNum)) {
        newSet.delete(palletNum);
      } else {
        newSet.add(palletNum);
      }
      return newSet;
    });
  };

  // Process scan queue - handles rapid scans sequentially
  const processScanQueue = React.useCallback(async () => {
    if (isProcessingRef.current) {
      return;
    }

    if (scanQueueRef.current.length === 0) {
      return;
    }

    isProcessingRef.current = true;

    while (scanQueueRef.current.length > 0) {
      const fnsku = scanQueueRef.current.shift();

      try {
        // Find the item
        const scannedItem = shipmentItems?.find(si =>
          si.item?.fnsku?.toLowerCase() === fnsku.toLowerCase()
        );

        if (!scannedItem) {
          playError();

          // Show error overlay
          setScanResult({
            success: false,
            message: `Item not found: ${fnsku}`,
            item: null
          });
          setShowScanOverlay(true);

          // Auto-dismiss after 5 seconds
          if (dismissTimerRef.current) {
            clearTimeout(dismissTimerRef.current);
          }
          dismissTimerRef.current = setTimeout(() => {
            setShowScanOverlay(false);
            dismissTimerRef.current = null;
          }, 5000);

          continue;
        }

        const currentBox = boxes.find(b => b.boxNumber === selectedBoxNumber);
        if (!currentBox) {
          playError();

          // Show error overlay
          setScanResult({
            success: false,
            message: `Box #${selectedBoxNumber} not found`,
            item: null
          });
          setShowScanOverlay(true);

          // Auto-dismiss after 5 seconds
          if (dismissTimerRef.current) {
            clearTimeout(dismissTimerRef.current);
          }
          dismissTimerRef.current = setTimeout(() => {
            setShowScanOverlay(false);
            dismissTimerRef.current = null;
          }, 5000);

          continue;
        }

        // Get weight info
        const asinInfo = effectiveAsinData?.find(data => data.ASIN === scannedItem.item.asin) || {};
        let itemWeight = asinInfo.WEIGHT || '';

        if (!itemWeight) {
          const matchingOutboundItem = shipmentItems?.find(si => si.item.asin === scannedItem.item.asin);
          if (matchingOutboundItem?.item?.weight) {
            itemWeight = matchingOutboundItem.item.weight;
          }
        }

        // Get current optimistic quantity (handles rapid scans)
        const sku = scannedItem.item.merchant_sku;
        const itemKey = `${selectedBoxNumber}_${sku}`;

        if (!optimisticQuantitiesRef.current[itemKey]) {
          const existingItem = currentBox?.items?.find(i => i.SKU === sku);
          optimisticQuantitiesRef.current[itemKey] = existingItem ? parseInt(existingItem.QTY) : 0;
        }

        // Increment optimistic quantity
        optimisticQuantitiesRef.current[itemKey] += 1;
        const newQty = optimisticQuantitiesRef.current[itemKey];

        // Calculate remaining in shipment
        // Total quantity for this item expected in shipment
        const totalExpected = scannedItem.quantity || 0;

        // Count how many of this item are already in ALL boxes (using optimistic counts)
        let totalInAllBoxes = 0;
        boxes.forEach(box => {
          const boxItemKey = `${box.boxNumber}_${sku}`;
          if (optimisticQuantitiesRef.current[boxItemKey]) {
            totalInAllBoxes += optimisticQuantitiesRef.current[boxItemKey];
          } else {
            const boxItem = box.items?.find(i => i.SKU === sku);
            if (boxItem) {
              totalInAllBoxes += parseInt(boxItem.QTY) || 0;
            }
          }
        });

        // Allow negative to show overages - don't clamp to 0
        const remainingInShipment = totalExpected - totalInAllBoxes;

        // Flash if overlay showing
        if (showScanOverlay) {
          setOverlayFlash(true);
          setTimeout(() => setOverlayFlash(false), 150);
        }

        // Calculate totals with optimistic quantities
        const totalUnits = Object.values(optimisticQuantitiesRef.current).reduce((sum, qty) => sum + qty, 0);

        // Calculate weight from items (not the box weight input)
        let calculatedWeight = 1; // Start with 1kg for box weight

        // Track which SKUs we've already counted
        const countedSkus = new Set();

        // Add weight from existing items in box
        currentBox?.items?.forEach(boxItem => {
          const itemAsin = boxItem.ASIN;
          const itemSku = boxItem.SKU;
          countedSkus.add(itemSku);

          const qty = itemSku === sku ? optimisticQuantitiesRef.current[itemKey] : parseInt(boxItem.QTY) || 0;

          // Find weight for this item
          const asinData = effectiveAsinData?.find(data => data.ASIN === itemAsin);
          const itemWeightKg = asinData?.WEIGHT ? parseFloat(asinData.WEIGHT) : 0;

          if (itemWeightKg > 0) {
            calculatedWeight += itemWeightKg * qty;
          }
        });

        // If this is a NEW item not yet in the box, add its weight
        if (!countedSkus.has(sku)) {
          const asinData = effectiveAsinData?.find(data => data.ASIN === scannedItem.item.asin);
          const itemWeightKg = asinData?.WEIGHT ? parseFloat(asinData.WEIGHT) :
                               (itemWeight ? parseFloat(itemWeight) : 0);
          if (itemWeightKg > 0) {
            calculatedWeight += itemWeightKg * newQty;
          }
        }

        // Show overlay with optimistic count
        setScanResult({
          success: true,
          item: {
            title: scannedItem.item.title,
            merchant_sku: sku,
            fnsku: scannedItem.item.fnsku,
            asin: scannedItem.item.asin,
            images: scannedItem.item.images
          },
          newQuantity: newQty,
          totalUnitsInBox: totalUnits,
          boxWeight: calculatedWeight,
          weightStatus: calculatedWeight >= 21 ? 'critical' : calculatedWeight >= 20 ? 'alert' : calculatedWeight >= 18 ? 'warning' : 'safe',
          remainingInShipment: remainingInShipment
        });
        setShowScanOverlay(true);

        setLastScannedItemSku(sku);
        setTimeout(() => setLastScannedItemSku(null), 2000);

        // Play success sound (item was added)
        playSuccess();

        // If weight is high, also play warning sound
        if (calculatedWeight > 20) {
          setTimeout(() => playWarning(), 300);
        }

        // Reset dismiss timer
        if (dismissTimerRef.current) {
          clearTimeout(dismissTimerRef.current);
        }
        dismissTimerRef.current = setTimeout(() => {
          setShowScanOverlay(false);
          dismissTimerRef.current = null;
        }, 5000);

        // Call API
        const newItem = {
          shipmentId,
          boxNumber: selectedBoxNumber,
          sku,
          asin: scannedItem.item.asin,
          fnsku: scannedItem.item.fnsku,
          qty: 1,
          length: currentBox.length,
          width: currentBox.width,
          height: currentBox.height,
          title: scannedItem.item.title,
          weight: itemWeight,
          pallet: currentBox.PALLET,
          employee: selectedNickname,
          images: scannedItem.item.images
        };

        const response = await fetch(`${API_URL}/api/add_item_to_box_outbound`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newItem),
        });

        const result = await response.json();

        if (!result.success) {
          playError();

          // Show error overlay
          setScanResult({
            success: false,
            message: result.error || 'Failed to add item to box',
            item: {
              title: scannedItem.item.title,
              merchant_sku: sku,
              fnsku: scannedItem.item.fnsku,
              asin: scannedItem.item.asin,
              images: scannedItem.item.images
            }
          });
          setShowScanOverlay(true);

          // Auto-dismiss after 5 seconds
          if (dismissTimerRef.current) {
            clearTimeout(dismissTimerRef.current);
          }
          dismissTimerRef.current = setTimeout(() => {
            setShowScanOverlay(false);
            dismissTimerRef.current = null;
          }, 5000);

          // Rollback optimistic quantity
          optimisticQuantitiesRef.current[itemKey] -= 1;
        }

      } catch (error) {
        playError();

        // Show error overlay
        setScanResult({
          success: false,
          message: 'Network error - please check your connection',
          item: null
        });
        setShowScanOverlay(true);

        // Auto-dismiss after 5 seconds
        if (dismissTimerRef.current) {
          clearTimeout(dismissTimerRef.current);
        }
        dismissTimerRef.current = setTimeout(() => {
          setShowScanOverlay(false);
          dismissTimerRef.current = null;
        }, 5000);
      }
    }

    // Refresh boxes after all scans processed
    await refreshBoxes();

    // Clear optimistic quantities after refresh
    optimisticQuantitiesRef.current = {};

    isProcessingRef.current = false;

    // Check if new items were added while processing
    if (scanQueueRef.current.length > 0) {
      processScanQueue();
    }
  }, [boxes, shipmentItems, selectedBoxNumber, effectiveAsinData, selectedNickname, shipmentId, playSuccess, playError, playWarning, showScanOverlay, refreshBoxes]);

  // Barcode scan handler - queues scans for sequential processing
  const handleBarcodeScan = React.useCallback((fnsku) => {
    // Only process scan if a box is selected
    if (!selectedBoxNumber) {
      playError();
      toast.error('Please select a box first');
      return;
    }

    // Find the scanned item to check if it has a weight
    const scannedItem = shipmentItems?.find(si =>
      si.item?.fnsku?.toLowerCase() === fnsku.toLowerCase()
    );

    if (!scannedItem) {
      playError();
      setScanResult({
        success: false,
        message: `Item not found: ${fnsku}`,
        item: null
      });
      setShowScanOverlay(true);

      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
      dismissTimerRef.current = setTimeout(() => {
        setShowScanOverlay(false);
        dismissTimerRef.current = null;
      }, 5000);
      return;
    }

    // Check if item has weight
    const asinInfo = effectiveAsinData?.find(data => data.ASIN === scannedItem.item.asin) || {};
    let itemWeight = asinInfo.WEIGHT || '';

    if (!itemWeight && scannedItem.item?.weight) {
      itemWeight = scannedItem.item.weight;
    }

    // If no weight, prompt for it
    if (!itemWeight || parseFloat(itemWeight) <= 0) {
      playWarning();
      setPendingScan(fnsku);
      setShowWeightModal(true);
      return;
    }

    // Add to queue
    scanQueueRef.current.push(fnsku);

    // Start processing
    processScanQueue();
  }, [selectedBoxNumber, boxes, shipmentItems, effectiveAsinData, playError, playWarning, processScanQueue, dismissTimerRef]);

  // Handle weight submission from modal
  const handleWeightSubmit = async (weight) => {
    // Close modal immediately for better UX
    setShowWeightModal(false);

    // Show large centered processing overlay
    setIsProcessingWeight(true);

    try {
      // Find the scanned item
      const scannedItem = shipmentItems?.find(si =>
        si.item?.fnsku?.toLowerCase() === pendingScan?.toLowerCase()
      );

      if (!scannedItem) {
        setIsProcessingWeight(false);
        toast.error('Item not found');
        return;
      }

      const asinIdentifier = scannedItem.item.asin;

      // Update the item weight in the database
      const response = await fetch(`${API_URL}/api/update_weight`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          asin: asinIdentifier,
          weight: weight,
          employee: selectedNickname
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update weight');
      }

      // Reload asin data to get the updated weight
      if (reloadAsinData) {
        await reloadAsinData();
      }

      // Reload shipment data to ensure OutboundItem displays updated weight
      if (reloadShipmentData) {
        await reloadShipmentData();
      }

      // Now directly add the item to the box (no need to rescan)
      if (pendingScan) {
        const currentBox = boxes.find(b => b.boxNumber === selectedBoxNumber);

        if (!currentBox) {
          setIsProcessingWeight(false);
          toast.error(`Box #${selectedBoxNumber} not found`);
          setPendingScan(null);
          return;
        }

        const sku = scannedItem.item.merchant_sku;

        // Prepare item data for adding to box
        const newItem = {
          shipmentId: shipmentId,
          boxNumber: selectedBoxNumber,
          sku,
          asin: scannedItem.item.asin,
          fnsku: scannedItem.item.fnsku,
          qty: 1,
          length: currentBox.length,
          width: currentBox.width,
          height: currentBox.height,
          title: scannedItem.item.title,
          weight: weight, // Use the newly set weight
          pallet: currentBox.PALLET,
          employee: selectedNickname,
          images: scannedItem.item.images
        };

        // Add item to box
        const addResponse = await fetch(`${API_URL}/api/add_item_to_box_outbound`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newItem),
        });

        const addResult = await addResponse.json();

        if (!addResult.success) {
          setIsProcessingWeight(false);
          playError();
          toast.error(addResult.error || 'Failed to add item to box');
          setPendingScan(null);
          return;
        }

        // Success! Refresh boxes and show success overlay
        await refreshBoxes();

        // Hide processing overlay
        setIsProcessingWeight(false);

        // Calculate remaining in shipment
        const totalExpected = scannedItem.quantity || 0;
        let totalInAllBoxes = 0;
        boxes.forEach(box => {
          const boxItem = box.items?.find(i => i.SKU === sku);
          if (boxItem) {
            totalInAllBoxes += parseInt(boxItem.QTY) || 0;
          }
        });
        totalInAllBoxes += 1; // Add the item we just scanned

        const remainingInShipment = totalExpected - totalInAllBoxes;

        // Show success overlay
        playSuccess();
        setScanResult({
          success: true,
          message: 'Item added successfully! (No rescan needed)',
          item: {
            title: scannedItem.item.title,
            merchant_sku: sku,
            fnsku: scannedItem.item.fnsku,
            asin: scannedItem.item.asin,
            images: scannedItem.item.images
          },
          quantity: 1,
          remainingInShipment: remainingInShipment
        });
        setShowScanOverlay(true);

        // Auto-dismiss after 3 seconds
        if (dismissTimerRef.current) {
          clearTimeout(dismissTimerRef.current);
        }
        dismissTimerRef.current = setTimeout(() => {
          setShowScanOverlay(false);
          dismissTimerRef.current = null;
        }, 3000);

        setPendingScan(null);
      }
    } catch (error) {
      console.error('Error setting item weight:', error);
      setIsProcessingWeight(false);
      toast.error('Failed to update weight');
      setPendingScan(null);
    }
  };

  // Auto-enable scanner when box is selected
  useEffect(() => {
    setScannerEnabled(!!selectedBoxNumber);
  }, [selectedBoxNumber]);

  // Initialize barcode scanner hook (disabled when Book Mode is active)
  useBarcodeScanner({
    onScan: handleBarcodeScan,
    enabled: scannerEnabled && !disableScanner,
    minLength: 10, // FNSKUs are typically 10+ characters
    maxDelay: 50
  });

  // Helper function to check if a box matches the search term
  const boxMatchesSearch = (box, searchTerm) => {
    if (!searchTerm.trim()) return true;
    
    const search = searchTerm.toLowerCase().trim();
    
    // Check box number
    const boxId = box.visualId || `#${box.boxNumber}`;
    if (boxId.toLowerCase().includes(search) || box.boxNumber.toString().includes(search)) {
      return true;
    }
    
    // Check items in the box
    return box.items.some(item => {
      // Check ASIN
      if (item.ASIN && item.ASIN.toLowerCase().includes(search)) return true;
      
      // Check FNSKU (both item.FNSKU and via shipment items)
      if (item.FNSKU && item.FNSKU.trim() && item.FNSKU.toLowerCase().includes(search)) return true;
      
      // Also check FNSKU from shipment items by matching ASIN
      const matchingShipmentItemForFnsku = shipmentItems?.find(si => si.item.asin === item.ASIN);
      if (matchingShipmentItemForFnsku?.item?.fnsku && 
          matchingShipmentItemForFnsku.item.fnsku.toLowerCase().includes(search)) {
        return true;
      }
      
      // Check SKU (box item SKU)
      if (item.SKU && item.SKU.toLowerCase().includes(search)) return true;
      
      // Check title for partial matches (both box item and shipment item titles)
      if (item.TITLE && item.TITLE.toLowerCase().includes(search)) return true;
      
      // ENHANCED: Also check against shipment item data by matching ASIN
      const matchingShipmentItem = shipmentItems?.find(si => si.item.asin === item.ASIN);
      if (matchingShipmentItem) {
        // Check merchant_sku
        if (matchingShipmentItem.item.merchant_sku && 
            matchingShipmentItem.item.merchant_sku.toLowerCase().includes(search)) {
          return true;
        }
        
        // Check title from shipment item (in case box item title is missing or different)
        if (matchingShipmentItem.item.title && 
            matchingShipmentItem.item.title.toLowerCase().includes(search)) {
          return true;
        }
      }
      
      return false;
    });
  };

  // Filter boxes based on error filter and search term - use effectiveAsinData instead of asinData
  let filteredBoxes = boxes || [];
  
  // Apply search filter
  if (searchTerm.trim()) {
    filteredBoxes = filteredBoxes.filter(box => boxMatchesSearch(box, searchTerm));
  }
  
  // Apply error filter
  if (showErrorsOnly) {
    filteredBoxes = filteredBoxes.filter(box => 
      getBoxValidationErrors(box, clientCountry, shipmentItems, effectiveAsinData).length > 0
    );
  }

  // Sort filtered boxes based on current sort order
  const sortedBoxes = [...filteredBoxes].sort((a, b) => {
    if (sortOrder === 'asc') {
      return a.boxNumber - b.boxNumber;
    } else {
      return b.boxNumber - a.boxNumber;
    }
  });

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const toggleErrorFilter = () => {
    setShowErrorsOnly(prev => !prev);
  };

  // Count boxes with errors for the button label
  const errorBoxCount = (boxes || []).filter(box => 
    getBoxValidationErrors(box, clientCountry, shipmentItems, effectiveAsinData).length > 0
  ).length;

  // Calculate freight class based on weight and standard pallet dimensions
  const calculateFreightClass = (weightKg) => {
    const weightLbs = weightKg * 2.20462; // Convert kg to lbs
    const palletDimensions = { length: 48, width: 40, height: 65 }; // inches
    const volumeCuFt = (palletDimensions.length * palletDimensions.width * palletDimensions.height) / 1728; // 1728 cubic inches = 1 cubic foot
    const density = weightLbs / volumeCuFt; // lbs per cubic foot
    
    // Standard freight class determination based on density
    if (density >= 30) return 50;
    else if (density >= 22.5) return 55;
    else if (density >= 15) return 60;
    else if (density >= 13.5) return 65;
    else if (density >= 12) return 70;
    else if (density >= 10.5) return 77.5;
    else if (density >= 9) return 85;
    else if (density >= 8) return 92.5;
    else if (density >= 7) return 100;
    else if (density >= 6) return 110;
    else if (density >= 5) return 125;
    else if (density >= 4) return 150;
    else if (density >= 3) return 175;
    else if (density >= 2) return 200;
    else if (density >= 1) return 250;
    else return 300;
  };

  // Calculate pallet summaries when in LTL mode
  const palletSummary = useMemo(() => {
    if (!isLTL || !boxes || boxes.length === 0) return null;

    const palletData = {};
    
    boxes.forEach(box => {
      const palletNum = box.PALLET || '1';
      if (!palletData[palletNum]) {
        palletData[palletNum] = {
          boxes: 0,
          totalWeight: 0,
          boxNumbers: []
        };
      }
      palletData[palletNum].boxes++;
      palletData[palletNum].totalWeight += parseFloat(box.weight) || 0;
      palletData[palletNum].boxNumbers.push(box.boxNumber);
    });

    // Add freight class calculation for each pallet
    Object.keys(palletData).forEach(palletNum => {
      palletData[palletNum].freightClass = calculateFreightClass(palletData[palletNum].totalWeight);
    });

    return palletData;
  }, [isLTL, boxes]);

  // Group boxes by pallet for display
  const groupedBoxes = useMemo(() => {
    if (!isLTL) return { ungrouped: sortedBoxes };
    
    const groups = {};
    sortedBoxes.forEach(box => {
      const palletNum = box.PALLET || '1';
      if (!groups[palletNum]) {
        groups[palletNum] = [];
      }
      groups[palletNum].push(box);
    });
    
    return groups;
  }, [isLTL, sortedBoxes]);

  // Copy pallet information to clipboard
  const copyPalletInfo = async () => {
    if (!palletSummary) return;

    const palletInfo = Object.entries(palletSummary)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([palletNum, data]) => {
        const weightLbs = (data.totalWeight * 2.20462).toFixed(2);
        return `Pallet ${palletNum}:
• Cartons: ${data.boxes}
• Weight: ${data.totalWeight.toFixed(2)} kg (${weightLbs} lbs)
• Freight Class: ${data.freightClass}
• Dimensions: 48" x 40" x 65" (Standard Pallet)
• Box Numbers: ${data.boxNumbers.sort((a, b) => a - b).join(', ')}`;
      })
      .join('\n\n');

    const totalWeight = Object.values(palletSummary).reduce((sum, data) => sum + data.totalWeight, 0);
    const totalBoxes = Object.values(palletSummary).reduce((sum, data) => sum + data.boxes, 0);
    const totalPallets = Object.keys(palletSummary).length;

    const fullInfo = `LTL SHIPMENT SUMMARY
========================

Total Pallets: ${totalPallets}
Total Cartons: ${totalBoxes}
Total Weight: ${totalWeight.toFixed(2)} kg (${(totalWeight * 2.20462).toFixed(2)} lbs)

${palletInfo}

Note: Freight classes calculated based on 48"x40"x65" pallet dimensions and weight density.`;

    try {
      await navigator.clipboard.writeText(fullInfo);
      toast({
        title: "Copied to Clipboard",
        description: "Pallet information copied successfully",
        variant: "success",
      });
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      toast({
        title: "Copy Failed",
        description: "Unable to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col h-full max-h-screen">
      {/* LTL Toggle and Enhanced Pallet Overview */}
      <div className="flex-shrink-0 px-4 py-2 bg-slate-100 border-b">
        <div className="space-y-2">
          {/* LTL Toggle Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm font-medium">LTL Shipment</span>
                <button
                  onClick={() => handleLTLToggle(!isLTL)}
                  disabled={loadingLTL}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isLTL ? 'bg-blue-600' : 'bg-gray-200'
                  } ${loadingLTL ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out ${
                      isLTL ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </label>
              
              {isLTL && palletSummary && (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setShowPalletOverview(!showPalletOverview)}
                    variant="outline"
                    size="sm"
                    className="h-7 px-3 text-xs bg-white hover:bg-slate-50 border-slate-300 text-slate-700"
                  >
                    <ChevronDown className={`w-3 h-3 mr-1 transition-transform ${showPalletOverview ? 'rotate-180' : ''}`} />
                    Pallet Overview ({Object.keys(palletSummary).length} pallets, {Object.values(palletSummary).reduce((sum, data) => sum + data.boxes, 0)} cartons)
                  </Button>
                  
                  <Button
                    onClick={copyPalletInfo}
                    variant="outline"
                    size="sm"
                    className="h-7 px-3 text-xs bg-white hover:bg-blue-50 border-blue-300 text-blue-700"
                  >
                    <ClipboardCopy className="w-3 h-3 mr-1" />
                    Copy Info
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Pallet Overview - Collapsible */}
          {isLTL && palletSummary && showPalletOverview && (
            <div className="bg-white rounded-lg p-4 border border-slate-300 shadow-sm animate-in slide-in-from-top-2 duration-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-800">Pallet Breakdown</h3>
                <div className="text-xs text-slate-600">
                  Total Weight: {Object.values(palletSummary).reduce((sum, data) => sum + data.totalWeight, 0).toFixed(2)} kg ({(Object.values(palletSummary).reduce((sum, data) => sum + data.totalWeight, 0) * 2.20462).toFixed(2)} lbs)
                </div>
              </div>
              
              <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(Object.keys(palletSummary).length, 4)}, 1fr)` }}>
                {Object.entries(palletSummary)
                  .sort(([a], [b]) => Number(a) - Number(b))
                  .map(([palletNum, data]) => (
                    <div key={palletNum} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-blue-800 text-sm">Pallet {palletNum}</span>
                        <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                          Class {data.freightClass}
                        </span>
                      </div>
                      
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Cartons:</span>
                          <span className="font-medium text-slate-800">{data.boxes}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Weight:</span>
                          <span className="font-medium text-slate-800">
                            {data.totalWeight.toFixed(2)} kg
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Weight (lbs):</span>
                          <span className="font-medium text-slate-800">
                            {(data.totalWeight * 2.20462).toFixed(2)} lbs
                          </span>
                        </div>
                        <div className="pt-1 border-t border-blue-200">
                          <div className="text-slate-600">Boxes:</div>
                          <div className="font-medium text-slate-800 leading-tight">
                            {data.boxNumbers.sort((a, b) => a - b).join(', ')}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              
              <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-600 text-center">
                Standard Pallet Dimensions: 48" × 40" × 65" | Freight classes calculated by weight density
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="flex-shrink-0 flex flex-wrap justify-between items-center px-4 py-2 border-b bg-slate-50 gap-2">
        <div className="flex items-center gap-3">
          <Button 
            onClick={toggleSortOrder}
            variant="outline"
            className="h-7 px-3 text-xs bg-white hover:bg-slate-100 border-slate-300"
          >
            Sort Box # {sortOrder === 'asc' ? '↑' : '↓'}
          </Button>
          {errorBoxCount > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-700">⚠ Show Errors ({errorBoxCount})</span>
              <button
                onClick={toggleErrorFilter}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 ${
                  showErrorsOnly ? 'bg-red-500' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out ${
                    showErrorsOnly ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}
        </div>
        
        <div className="flex-1">
          <div className="relative max-w-md">
            <input
              type="text"
              placeholder="Search ASIN, FNSKU, SKU, or box..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-7 px-3 pr-8 text-xs bg-white border border-slate-300 hover:border-slate-400 focus:border-blue-500 focus:outline-none w-full"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Boxes List */}
      <div className="flex-1 overflow-y-auto p-3 bg-white min-h-0">
        {sortedBoxes.length === 0 && !searchTerm.trim() && !showErrorsOnly ? (
          <div className="flex flex-col items-center gap-4">
            <p className="text-sm text-slate-500">
              No boxes created yet. Click \"+ Add Box\" to start.
            </p>
            <AddBoxPlaceholder onClick={onAddBox} />
          </div>
        ) : searchTerm.trim() && sortedBoxes.length === 0 ? (
          <p className="text-sm text-slate-500">
            No boxes found matching \"{searchTerm}\". Try a different search term.
          </p>
        ) : showErrorsOnly && sortedBoxes.length === 0 ? (
          <p className="text-sm text-slate-500">
            No boxes with errors found.
          </p>
        ) : (
          <div className="space-y-1">
            {/* Add placeholder at beginning if reverse order and no filters active */}
            {sortOrder === 'desc' && !searchTerm.trim() && !showErrorsOnly && (
              <AddBoxPlaceholder onClick={onAddBox} />
            )}
            
            {isLTL ? (
              // Render with pallet separation
              Object.entries(groupedBoxes)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([palletNum, palletBoxes]) => (
                  <div key={palletNum}>
                    {palletNum !== 'ungrouped' && (
                      <div 
                        className="mt-4 mb-2 px-3 py-2 bg-blue-50 border-l-4 border-blue-400 cursor-pointer hover:bg-blue-100 transition-colors"
                        onClick={() => togglePalletCollapse(palletNum)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-semibold text-blue-700">
                            Pallet {palletNum} - {palletBoxes.length} boxes, {palletBoxes.reduce((sum, b) => sum + (parseFloat(b.weight) || 0), 0).toFixed(2)}kg
                          </div>
                          <div className="text-blue-600">
                            {collapsedPallets.has(palletNum) ? (
                              <ChevronDown className="w-4 h-4 transform rotate-180" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {!collapsedPallets.has(palletNum) && palletBoxes.map((box, index) => (
                      <div key={box.boxNumber}>
                        <BoxListItem
                          box={box}
                          selected={box.boxNumber === selectedBoxNumber}
                          onClick={() => setSelectedBoxNumber(box.boxNumber === selectedBoxNumber ? null : box.boxNumber)}
                          shipmentItems={shipmentItems}
                          asinData={effectiveAsinData}
                          allBoxes={sortedBoxes}
                          onHoverChange={setHoverPreview}
                          clientCountry={clientCountry}
                          standardBoxes={standardBoxes}
                          isLTL={isLTL}
                          onPalletChange={handlePalletChange}
                        />
                        
                        {/* Accordion Content */}
                        {box.boxNumber === selectedBoxNumber && (
                          <BoxAccordion
                            box={box}
                            onDuplicateBox={onDuplicateBox}
                            onDeleteBox={async () => {
                              if (window.confirm('Delete this box? All boxes after this one will be renumbered to maintain sequence.')) {
                                try {
                                  const requestData = {
                                    outbound_id: shipmentId,
                                    boxNumber: box.boxNumber,
                                    employee: selectedNickname
                                  };
                                  
                                  const response = await fetch(`${API_URL}/api/delete_box_outbound`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify(requestData)
                                  });

                                  if (!response.ok) {
                                    const errorData = await response.json();
                                    throw new Error(errorData.error || 'Failed to delete box');
                                  }

                                  setSelectedBoxNumber(null); // Close accordion
                                  
                                  if (refreshBoxes) {
                                    await refreshBoxes();
                                  }
                                } catch (error) {
                                  console.error('Error deleting box:', error);
                                  alert(`Failed to delete box: ${error.message}`);
                                }
                              }
                            }}
                            onUpdateBoxDetails={onUpdateBoxDetails}
                            shipmentItems={shipmentItems}
                            asinData={effectiveAsinData}
                            selectedNickname={selectedNickname}
                            clientCountry={clientCountry}
                            standardBoxes={standardBoxes}
                            fetchStandardBoxes={fetchStandardBoxes}
                            shipmentId={shipmentId}
                            onClose={() => setSelectedBoxNumber(null)}
                            setShipmentItems={setShipmentItems}
                            canEditDetails={canEditDetails}
                            isLTL={isLTL}
                            onPalletChange={handlePalletChange}
                            lastScannedItemSku={lastScannedItemSku}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ))
            ) : (
              // Normal rendering without pallet separation
              sortedBoxes.map((box, index) => (
                <div key={box.boxNumber}>
                  <BoxListItem
                    box={box}
                    selected={box.boxNumber === selectedBoxNumber}
                    onClick={() => setSelectedBoxNumber(box.boxNumber === selectedBoxNumber ? null : box.boxNumber)}
                    shipmentItems={shipmentItems}
                    asinData={effectiveAsinData}
                    allBoxes={sortedBoxes}
                    onHoverChange={setHoverPreview}
                    clientCountry={clientCountry}
                    standardBoxes={standardBoxes}
                    isLTL={isLTL}
                    onPalletChange={handlePalletChange}
                  />
                  
                  {/* Accordion Content */}
                  {box.boxNumber === selectedBoxNumber && (
                    <BoxAccordion
                      box={box}
                      onDuplicateBox={onDuplicateBox}
                      onDeleteBox={async () => {
                        if (window.confirm('Delete this box? All boxes after this one will be renumbered to maintain sequence.')) {
                          try {
                            const requestData = {
                              outbound_id: shipmentId,
                              boxNumber: box.boxNumber,
                              employee: selectedNickname
                            };
                            
                            const response = await fetch(`${API_URL}/api/delete_box_outbound`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify(requestData)
                            });

                            if (!response.ok) {
                              const errorData = await response.json();
                              throw new Error(errorData.error || 'Failed to delete box');
                            }

                            setSelectedBoxNumber(null); // Close accordion
                            
                            if (refreshBoxes) {
                              await refreshBoxes();
                            }
                          } catch (error) {
                            console.error('Error deleting box:', error);
                            alert(`Failed to delete box: ${error.message}`);
                          }
                        }
                      }}
                      onUpdateBoxDetails={onUpdateBoxDetails}
                      shipmentItems={shipmentItems}
                      asinData={effectiveAsinData}
                      selectedNickname={selectedNickname}
                      clientCountry={clientCountry}
                      standardBoxes={standardBoxes}
                      fetchStandardBoxes={fetchStandardBoxes}
                      shipmentId={shipmentId}
                      onClose={() => setSelectedBoxNumber(null)}
                      setShipmentItems={setShipmentItems}
                      canEditDetails={canEditDetails}
                      isLTL={isLTL}
                      onPalletChange={handlePalletChange}
                      lastScannedItemSku={lastScannedItemSku}
                    />
                  )}
                </div>
              ))
            )}
            
            {/* Add placeholder at end if normal order and no filters active */}
            {sortOrder === 'asc' && !searchTerm.trim() && !showErrorsOnly && (
              <AddBoxPlaceholder onClick={onAddBox} />
            )}
          </div>
        )}
      </div>



      {/* Global Hover Preview Tooltip */}
      {hoverPreview && hoverPreview.box.items && hoverPreview.box.items.length > 0 && (
        <div
          className="fixed bg-white border-2 border-blue-300 shadow-xl rounded-lg p-3 w-80 max-w-sm"
          style={{
            left: `${hoverPreview.position.x}px`,
            pointerEvents: 'none',
            zIndex: 99999,
            ...(hoverPreview.position.y !== undefined && { top: `${hoverPreview.position.y}px` }),
            ...(hoverPreview.position.bottom !== undefined && { bottom: `${hoverPreview.position.bottom}px` }),
          }}
        >
          {/* Arrow indicator based on placement */}
          {hoverPreview.position.placement === 'right' && (
            <div 
              className="absolute right-full w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[8px] border-r-blue-300 transform -translate-y-1/2"
              style={{ top: hoverPreview.position.arrowVerticalOffset ? `${hoverPreview.position.arrowVerticalOffset}px` : '50%' }}
            ></div>
          )}
          {hoverPreview.position.placement === 'left' && (
            <div 
              className="absolute left-full w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[8px] border-l-blue-300 transform -translate-y-1/2"
              style={{ top: hoverPreview.position.arrowVerticalOffset ? `${hoverPreview.position.arrowVerticalOffset}px` : '50%' }}
            ></div>
          )}
          {hoverPreview.position.placement === 'below' && (
            <div 
              className="absolute bottom-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[8px] border-b-blue-300 transform -translate-x-1/2"
              style={{ left: hoverPreview.position.arrowOffset ? `${hoverPreview.position.arrowOffset}px` : '50%' }}
            ></div>
          )}
          {hoverPreview.position.placement === 'above' && (
            <div 
              className="absolute top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-blue-300 transform -translate-x-1/2"
              style={{ left: hoverPreview.position.arrowOffset ? `${hoverPreview.position.arrowOffset}px` : '50%' }}
            ></div>
          )}
          
          <div className="text-sm font-bold text-gray-900 mb-2 border-b border-gray-300 pb-1">
            📦 {hoverPreview.box.visualId || `#${hoverPreview.box.boxNumber}`} Contents
            {(() => {
              const duplicateCount = hoverPreview.allBoxes?.filter(b => getBoxSignature(b) === hoverPreview.boxSignature).length || 1;
              const boxColor = getBoxColor(hoverPreview.boxSignature, hoverPreview.allBoxes, 0);
              return duplicateCount > 1 ? (
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${boxColor.bg} ${boxColor.text}`}>
                  {duplicateCount} identical boxes
                </span>
              ) : null;
            })()}
          </div>
          
          {/* Validation Errors */}
          {hoverPreview.validationErrors && hoverPreview.validationErrors.length > 0 && (
            <div className="mb-2 p-2 bg-red-50 border border-red-200 text-red-800">
              <div className="text-xs font-medium mb-1 flex items-center gap-1">
                <span className="text-red-600">⚠</span>
                Validation Issues:
              </div>
              <ul className="text-xs space-y-0.5">
                {hoverPreview.validationErrors.map((error, index) => (
                  <li key={index} className="text-red-700">• {error}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Box dimensions and weight */}
          {(hoverPreview.box.length || hoverPreview.box.width || hoverPreview.box.height || hoverPreview.box.weight) && (
            <div className="text-xs text-gray-700 mb-2 bg-gray-50 px-2 py-1 border border-gray-200">
              {[
                (hoverPreview.box.length || hoverPreview.box.width || hoverPreview.box.height) && 
                  `${Math.round(hoverPreview.box.length || 0)} × ${Math.round(hoverPreview.box.width || 0)} × ${Math.round(hoverPreview.box.height || 0)}`,
                hoverPreview.box.weight && `${Math.round(hoverPreview.box.weight)} kg`
              ].filter(Boolean).join(' • ')}
            </div>
          )}
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {[...new Set(hoverPreview.box.items.map(item => item.ASIN))].map((asin, index) => {
              const itemDetails = hoverPreview.getItemDetails(asin);
              return (
                <div key={index} className="flex items-center space-x-2 p-1 hover:bg-gray-50">
                  <div className="w-8 h-8 bg-gray-100 border border-gray-300 overflow-hidden flex-shrink-0">
                    <img 
                      src={itemDetails.imageUrl} 
                      alt={asin}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-900 truncate">
                      {itemDetails.title}
                    </div>
                    <div className="text-[10px] text-gray-600 truncate">
                      {itemDetails.sku}
                    </div>
                  </div>
                  <div className="text-xs font-bold text-blue-600 flex-shrink-0 bg-blue-50 px-1.5 py-0.5">
                    {itemDetails.quantity}x
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-xs font-semibold text-gray-700 mt-2 pt-1 border-t border-gray-300 bg-gray-50 px-1.5 py-0.5 text-center">
            Total: {getBoxStats(hoverPreview.box).totalUnits} units
          </div>
        </div>
      )}

      {/* Barcode Scanner Overlay */}
      <BarcodeScannerOverlay
        isVisible={showScanOverlay}
        onClose={() => setShowScanOverlay(false)}
        scanResult={scanResult}
        boxNumber={selectedBoxNumber}
        boxDimensions={(() => {
          const box = boxes.find(b => b.boxNumber === selectedBoxNumber);
          if (!box) return null;
          const l = box.length || 0;
          const w = box.width || 0;
          const h = box.height || 0;
          if (l && w && h) {
            return `${l} × ${w} × ${h} cm`;
          }
          return null;
        })()}
        boxName={(() => {
          const box = boxes.find(b => b.boxNumber === selectedBoxNumber);
          return box?.NAME || null;
        })()}
        currentBoxItems={(boxes.find(b => b.boxNumber === selectedBoxNumber)?.items || []).map(item => {
          // Enrich each item with image URL from shipmentItems or asinData
          const shipmentItem = shipmentItems?.find(si => si.item.asin === item.ASIN);
          const asinInfo = effectiveAsinData?.find(a => a.ASIN === item.ASIN);
          return {
            ...item,
            IMAGE_URL: shipmentItem?.item?.images?.[0]?.large_url || asinInfo?.IMAGE_URL || null
          };
        })}
        shouldFlash={overlayFlash}
      />

      {/* Weight Input Modal */}
      <WeightInputModal
        isOpen={showWeightModal}
        onSubmit={handleWeightSubmit}
        onCancel={() => {
          setShowWeightModal(false);
          setPendingScan(null);
        }}
        boxNumber={selectedBoxNumber}
        itemTitle={
          pendingScan
            ? shipmentItems?.find(si => si.item?.fnsku?.toLowerCase() === pendingScan.toLowerCase())?.item?.title
            : ''
        }
      />

      {/* Scanner Active Indicator (optional - can be hidden) */}
      {scannerEnabled && (
        <div className="fixed bottom-4 left-4 z-40 bg-green-500 text-white px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2 text-xs font-medium animate-pulse">
          <Scan className="w-3 h-3" />
          Scanner Active - Box #{selectedBoxNumber}
        </div>
      )}

      {/* Large centered processing overlay */}
      {isProcessingWeight && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-6 min-w-[400px]">
            <div className="relative">
              <svg className="animate-spin h-20 w-20 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <Package className="w-8 h-8 text-blue-600 animate-pulse" />
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Processing...</h3>
              <p className="text-gray-600 text-lg">Updating weight and adding to box</p>
              <p className="text-gray-400 text-sm mt-2">Please wait, this will only take a moment</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoxesPanel; 